import { useState, useEffect, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  FiArrowLeft,
  FiDownload,
  FiClock,
  FiCheckCircle,
  FiAlertCircle,
  FiCopy,
  FiCheck,
  FiChevronLeft,
  FiChevronRight,
  FiChevronDown,
  FiInfo,
  FiX,
  FiCode,
  FiEye,
  FiMaximize2,
  FiSearch,
  FiBook,
  FiFilter,
  FiRefreshCw,
  FiShuffle,
  FiRepeat,
} from "react-icons/fi";
import PropTypes from "prop-types";
import {
  ComposedChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Cell,
  Rectangle,
} from "recharts";
import {
  fetchRun,
  fetchRelatedRuns,
  triggerVarietyCheck,
  updateSkipVarietyCheck,
} from "../services/runService";
import { getSubjectConfig } from "../utils/subjectConfig";
import {
  getCurriculumConfig,
  getTypeDisplayName,
  getRequestCategoryConfig,
  getModifierBadges,
} from "../utils/curriculumConfig";
import { fetchCurriculumItem } from "../services/curriculumApiService";
import RerunButton from "../components/run/RerunButton";
import ReEvaluateButton from "../components/run/ReEvaluateButton";
import { GenerationColumn } from "@trilogy-innovations/content-renderers";

// Multi-Select Dropdown Component for filters
const MultiSelectDropdown = ({
  options,
  selectedValues,
  onChange,
  placeholder,
  width = "w-36",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleOption = (value) => {
    if (selectedValues.includes(value)) {
      onChange(selectedValues.filter((v) => v !== value));
    } else {
      onChange([...selectedValues, value]);
    }
  };

  const hasSelection = selectedValues.length > 0;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`flex ${width} items-center justify-between rounded-lg border bg-white px-3 py-2 text-sm transition-colors focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-200 ${
          hasSelection
            ? "border-primary-300 text-primary-700"
            : "border-gray-300 text-gray-500 hover:border-gray-400"
        }`}
      >
        <span className="truncate">{placeholder}</span>
        <FiChevronDown
          className={`ml-2 size-4 shrink-0 transition-transform ${
            hasSelection ? "text-primary-500" : "text-gray-400"
          } ${isOpen ? "rotate-180" : ""}`}
        />
      </button>

      {isOpen && (
        <div className="absolute inset-x-0 z-50 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-gray-300 bg-white shadow-lg">
          {options.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-500">
              No options available
            </div>
          ) : (
            options.map((option) => {
              const isSelected = selectedValues.includes(option.value);
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => toggleOption(option.value)}
                  className={`flex w-full items-center justify-between px-3 py-2 text-left text-sm transition-colors hover:bg-gray-50 ${
                    isSelected ? "bg-primary-50" : ""
                  }`}
                >
                  <span className="flex-1">{option.label}</span>
                  {isSelected && (
                    <FiCheck className="ml-2 size-4 shrink-0 text-primary-600" />
                  )}
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

MultiSelectDropdown.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
  selectedValues: PropTypes.arrayOf(PropTypes.string).isRequired,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string.isRequired,
  width: PropTypes.string,
};

// Helper to format date/time concisely
const formatDateTime = (dateString) => {
  if (!dateString) {
    return "-";
  }
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

// Status badge configuration
const getStatusConfig = (status) => {
  switch (status) {
    case "completed":
      return {
        label: "Completed",
        bgColor: "bg-green-100",
        textColor: "text-green-800",
        icon: FiCheckCircle,
      };
    case "failed":
      return {
        label: "Failed",
        bgColor: "bg-red-100",
        textColor: "text-red-800",
        icon: FiAlertCircle,
      };
    case "generating":
      return {
        label: "Generating",
        bgColor: "bg-blue-100",
        textColor: "text-blue-800",
        dotColor: "bg-blue-500",
        icon: FiClock,
      };
    case "evaluating":
      return {
        label: "Evaluating",
        bgColor: "bg-purple-100",
        textColor: "text-purple-800",
        dotColor: "bg-purple-500",
        icon: FiClock,
      };
    case "queued":
      return {
        label: "Queued",
        bgColor: "bg-yellow-100",
        textColor: "text-yellow-800",
        dotColor: "bg-yellow-500",
        icon: FiClock,
      };
    default:
      return {
        label: status || "Unknown",
        bgColor: "bg-gray-100",
        textColor: "text-gray-800",
        icon: FiClock,
      };
  }
};

// Calculate time taken between two dates
/**
 * Calculate the human-readable duration between two ISO date strings.
 * @param {string|null} startDate - ISO 8601 start timestamp (e.g. run.started_at)
 * @param {string|null} endDate   - ISO 8601 end timestamp (e.g. run.completed_at)
 * @returns {string|null} Formatted duration (e.g. "3m 25s"), or null if either
 *   argument is falsy or endDate precedes startDate.
 */
export const calculateTimeTaken = (startDate, endDate) => {
  if (!startDate || !endDate) {
    return null;
  }
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffMs = end - start;

  if (diffMs < 0) {
    return null;
  }

  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);

  if (diffHours > 0) {
    const remainingMins = diffMins % 60;
    const remainingSecs = diffSecs % 60;
    return `${diffHours}h ${remainingMins}m ${remainingSecs}s`;
  } else if (diffMins > 0) {
    const remainingSecs = diffSecs % 60;
    return `${diffMins}m ${remainingSecs}s`;
  } else {
    return `${diffSecs}s`;
  }
};

// Format a duration in milliseconds to a human-readable string
/**
 * Format a duration in milliseconds to a human-readable string.
 * @param {number|null} ms - Duration in milliseconds. Must be a positive number.
 * @returns {string|null} Formatted duration (e.g. "45s", "3m 25s", "2h 31m 0s"),
 *   or null if ms is falsy or negative.
 */
export const formatDurationMs = (ms) => {
  if (!ms || ms < 0) {
    return null;
  }
  const diffSecs = Math.floor(ms / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours > 0) {
    const remainingMins = diffMins % 60;
    const remainingSecs = diffSecs % 60;
    return `${diffHours}h ${remainingMins}m ${remainingSecs}s`;
  } else if (diffMins > 0) {
    const remainingSecs = diffSecs % 60;
    return `${diffMins}m ${remainingSecs}s`;
  }
  return `${diffSecs}s`;
};

// Run Details Section Component
const RunDetailsSection = ({ run }) => {
  const isInProgress = ["generating", "evaluating", "queued"].includes(
    run.status
  );

  // Live duration ticker for in-progress runs
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!isInProgress || !run.started_at) {
      return;
    }
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [isInProgress, run.started_at]);

  // Calculate errors
  const totalItems = run.total_items || 0;
  const itemsGenerated = run.items_generated || 0;
  const itemsEvaluated = run.items_evaluated || 0;

  // Error counts must come from error_message first — the worker
  // double-books generation failures into items_evaluated (score 0,
  // error null) so the gap-based formula (items_generated -
  // items_evaluated) always reads 0 even when there are real failures.
  // Fall back to the gaps for items the worker never attempted (e.g.
  // crash partway through), which don't appear in error_message.
  // Mirrors the logic in src/components/run/RunProgress.js.
  const { generationErrors, evaluationErrors, totalErrors } = useMemo(() => {
    let genCount = 0;
    let evalCount = 0;

    if (run.error_message) {
      try {
        const errorData = JSON.parse(run.error_message);
        genCount = errorData.generation_errors
          ? Object.keys(errorData.generation_errors).length
          : 0;
        evalCount = errorData.evaluation_errors
          ? Object.keys(errorData.evaluation_errors).length
          : 0;
      } catch {
        // Parse failed — fall through to gap-based counts
      }
    }

    const isTerminal = run.status === "completed" || run.status === "failed";
    if (isTerminal) {
      genCount = Math.max(genCount, Math.max(0, totalItems - itemsGenerated));
      evalCount = Math.max(
        evalCount,
        Math.max(0, itemsGenerated - itemsEvaluated)
      );
    }

    return {
      generationErrors: genCount,
      evaluationErrors: evalCount,
      totalErrors: genCount + evalCount,
    };
  }, [run, totalItems, itemsGenerated, itemsEvaluated]);

  // Format grades and subjects
  const grades = run.filters?.grades || [];
  const subjects = run.filters?.subjects || [];

  // Calculate time taken - live ticker for in-progress, static for completed
  const timeTaken = isInProgress
    ? run.started_at
      ? formatDurationMs(now - new Date(run.started_at).getTime())
      : null
    : calculateTimeTaken(run.started_at, run.completed_at);

  // Progress percentages for in-progress view
  const generationPct =
    totalItems > 0 ? Math.round((itemsGenerated / totalItems) * 100) : 0;
  const evaluationPct =
    totalItems > 0 ? Math.round((itemsEvaluated / totalItems) * 100) : 0;

  // Compute live aggregate score and pass rate from raw results during in-progress
  const { liveAggregateScore, livePassRate } = useMemo(() => {
    if (!isInProgress) {
      return {
        liveAggregateScore: run.aggregate_score
          ? parseFloat(run.aggregate_score)
          : null,
        livePassRate: run.pass_rate ? parseFloat(run.pass_rate) : null,
      };
    }

    const genResults = run.raw_generation_results || {};
    const evalResults = run.raw_evaluation_results || {};

    // Count all items where generation succeeded (including those with eval errors)
    // Items with eval errors (e.g., blank/empty questions) should be penalized as score=0
    const generatedKeys = Object.keys(genResults).filter(
      (key) => !genResults[key]?.error
    );

    if (generatedKeys.length === 0) {
      return { liveAggregateScore: null, livePassRate: null };
    }

    // Denominator = items that have received an eval result (success or error).
    // Items not yet evaluated are simply pending — exclude them so an in-progress
    // run doesn't artificially drag the score/pass-rate down.
    // Eval errors (e.g. blank questions) are real failures and count as score=0.
    const evaluatedOrErroredKeys = generatedKeys.filter(
      (key) => evalResults[key] !== undefined
    );

    if (evaluatedOrErroredKeys.length === 0) {
      return { liveAggregateScore: null, livePassRate: null };
    }

    const denominator = evaluatedOrErroredKeys.length;

    const totalScore = evaluatedOrErroredKeys.reduce((sum, key) => {
      const evalResult = evalResults[key];
      if (
        evalResult &&
        !evalResult.error &&
        typeof evalResult.score === "number"
      ) {
        return sum + evalResult.score;
      }
      return sum; // eval error → score 0
    }, 0);

    const passedCount = evaluatedOrErroredKeys.filter((key) => {
      const evalResult = evalResults[key];
      return evalResult && !evalResult.error && evalResult.passed;
    }).length;

    return {
      liveAggregateScore: totalScore / denominator,
      livePassRate: passedCount / denominator,
    };
  }, [
    isInProgress,
    run.aggregate_score,
    run.pass_rate,
    run.raw_evaluation_results,
    run.raw_generation_results,
  ]);

  // Calculate average generation latency from raw_generation_results
  const avgGenerationLatency = useMemo(() => {
    const generations = run?.raw_generation_results;
    if (!generations) {
      return null;
    }
    const times = Object.values(generations)
      .map((g) => g?.timeTaken)
      .filter((t) => t !== null && t !== undefined);
    if (times.length === 0) {
      return null;
    }
    const avg = times.reduce((sum, t) => sum + t, 0) / times.length;
    return avg.toFixed(2);
  }, [run]);

  return (
    <div className="bg-gray-50 px-6 py-5">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        {/* Run Info Card */}
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <h4 className="mb-3 text-xs font-semibold uppercase tracking-wider text-gray-400">
            Run Info
          </h4>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">Generator</span>
              <span className="text-sm font-medium text-gray-900">
                {run.bm_generators?.name || "[Deleted]"}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">Curriculum</span>
              {run.filters?.curriculum ? (
                <span
                  className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${getCurriculumConfig(run.filters?.curriculum).bgColor} ${getCurriculumConfig(run.filters?.curriculum).color}`}
                >
                  {getCurriculumConfig(run.filters?.curriculum).abbreviation}
                </span>
              ) : (
                <span className="text-sm text-gray-900">-</span>
              )}
            </div>
            {run.filters?.request_category && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Category</span>
                <div className="flex flex-wrap justify-end gap-1">
                  {(() => {
                    const catConfig = getRequestCategoryConfig(
                      run.filters.request_category
                    );
                    return (
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${catConfig.bgColor} ${catConfig.color}`}
                      >
                        {catConfig.label}
                      </span>
                    );
                  })()}
                  {getModifierBadges(run.filters).map((badge) => (
                    <span
                      key={badge.key}
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${badge.bgColor} ${badge.color}`}
                    >
                      {badge.label}
                    </span>
                  ))}
                </div>
              </div>
            )}
            <div className="flex items-start justify-between">
              <span className="text-sm text-gray-500">Grades</span>
              <div className="flex flex-wrap justify-end gap-1">
                {grades.length > 0 ? (
                  grades.map((grade, idx) => (
                    <span
                      key={idx}
                      className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800"
                    >
                      {grade === "K" ? "K" : grade}
                    </span>
                  ))
                ) : (
                  <span className="text-sm text-gray-900">-</span>
                )}
              </div>
            </div>
            <div className="flex items-start justify-between">
              <span className="text-sm text-gray-500">Subjects</span>
              <div className="flex flex-wrap justify-end gap-1">
                {subjects.length > 0 ? (
                  subjects.map((subject, idx) => {
                    const subjectConfig = getSubjectConfig(subject);
                    return (
                      <span
                        key={idx}
                        className={`inline-flex items-center rounded-full ${subjectConfig.bgColor} px-2 py-0.5 text-xs font-medium ${subjectConfig.color}`}
                      >
                        {subject.charAt(0).toUpperCase() +
                          subject.slice(1).toLowerCase()}
                      </span>
                    );
                  })
                ) : (
                  <span className="text-sm text-gray-900">-</span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Timing Card */}
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <h4 className="mb-3 text-xs font-semibold uppercase tracking-wider text-gray-400">
            Timing
          </h4>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">Created</span>
              <span className="text-sm text-gray-900">
                {formatDateTime(run.created_at)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">Completed</span>
              <span className="text-sm text-gray-900">
                {isInProgress ? "—" : formatDateTime(run.completed_at)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">Duration</span>
              <span className="text-sm font-medium text-gray-900">
                {timeTaken || "-"}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">
                Avg Generation Latency
              </span>
              <span className="text-sm font-medium text-gray-900">
                {avgGenerationLatency ? `${avgGenerationLatency}s` : "-"}
              </span>
            </div>
          </div>
        </div>

        {/* Progress Card */}
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <h4 className="mb-3 text-xs font-semibold uppercase tracking-wider text-gray-400">
            Progress
          </h4>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">Total Items</span>
              <span className="text-sm text-gray-900">{totalItems || "-"}</span>
            </div>
            {isInProgress ? (
              <>
                {/* Generation with inline progress bar */}
                <div className="flex items-center justify-between gap-2">
                  <span className="shrink-0 text-sm text-gray-500">
                    Generation
                  </span>
                  <div className="flex items-center gap-2">
                    <div className="h-1.5 w-16 overflow-hidden rounded-full bg-gray-100">
                      <div
                        className="h-full rounded-full bg-blue-500 transition-all duration-500"
                        style={{ width: `${generationPct}%` }}
                      />
                    </div>
                    <span className="w-8 text-right text-xs font-medium text-blue-600">
                      {generationPct}%
                    </span>
                    <span className="whitespace-nowrap text-sm text-gray-900">
                      {itemsGenerated}
                      <span className="text-xs text-gray-400">
                        {" "}
                        / {totalItems}
                      </span>
                    </span>
                  </div>
                </div>
                {/* Evaluation with inline progress bar */}
                <div className="flex items-center justify-between gap-2">
                  <span className="shrink-0 text-sm text-gray-500">
                    Evaluation
                  </span>
                  <div className="flex items-center gap-2">
                    <div className="h-1.5 w-16 overflow-hidden rounded-full bg-gray-100">
                      <div
                        className="h-full rounded-full bg-green-500 transition-all duration-500"
                        style={{ width: `${evaluationPct}%` }}
                      />
                    </div>
                    <span className="w-8 text-right text-xs font-medium text-green-600">
                      {evaluationPct}%
                    </span>
                    <span className="whitespace-nowrap text-sm text-gray-900">
                      {itemsEvaluated}
                      <span className="text-xs text-gray-400">
                        {" "}
                        / {totalItems}
                      </span>
                    </span>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Generated</span>
                  <span className="text-sm text-gray-900">
                    {itemsGenerated}
                    {totalItems > 0 && (
                      <span className="text-xs text-gray-400">
                        {" "}
                        / {totalItems}
                      </span>
                    )}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Evaluated</span>
                  <span className="text-sm text-gray-900">
                    {itemsEvaluated}
                    {itemsGenerated > 0 && (
                      <span className="text-xs text-gray-400">
                        {" "}
                        / {itemsGenerated}
                      </span>
                    )}
                  </span>
                </div>
              </>
            )}
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">Errors</span>
              {totalErrors > 0 ? (
                <span className="inline-flex items-center gap-1 text-sm font-medium text-red-600">
                  <FiAlertCircle className="size-3" />
                  {totalErrors}
                  <span className="text-xs font-normal text-gray-400">
                    ({generationErrors}g, {evaluationErrors}e)
                  </span>
                </span>
              ) : (
                <span className="text-sm text-green-600">None</span>
              )}
            </div>
          </div>
        </div>

        {/* Results Card */}
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <div className="mb-3">
            <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-400">
              Results
            </h4>
            <p className="mt-0.5 text-[10px] text-gray-400">
              Based on {itemsEvaluated} evaluated items
            </p>
          </div>
          <div className="space-y-3">
            <div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Aggregate Score</span>
                <span className="text-lg font-semibold text-gray-900">
                  {liveAggregateScore !== null
                    ? `${(liveAggregateScore * 100).toFixed(2)}%`
                    : "-"}
                </span>
              </div>
              {liveAggregateScore !== null && (
                <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
                  <div
                    className="h-full rounded-full bg-primary-500"
                    style={{
                      width: `${liveAggregateScore * 100}%`,
                    }}
                  />
                </div>
              )}
            </div>
            <div>
              <div className="flex items-center justify-between">
                <span className="group relative flex items-center gap-1 text-sm text-gray-500">
                  Pass Rate
                  <span className="cursor-help">
                    <FiInfo className="size-3.5 text-gray-400" />
                  </span>
                  {/* Tooltip */}
                  <span className="pointer-events-none absolute bottom-full left-0 mb-2 w-48 rounded-lg bg-gray-900 px-3 py-2 text-xs text-white opacity-0 shadow-lg transition-opacity group-hover:opacity-100">
                    Percentage of items with an overall score ≥85% out of all
                    generation requests. Failed generations count as 0.
                    <span className="absolute left-4 top-full border-4 border-transparent border-t-gray-900"></span>
                  </span>
                </span>
                <span className="text-lg font-semibold text-gray-900">
                  {livePassRate !== null
                    ? `${(livePassRate * 100).toFixed(2)}%`
                    : "-"}
                </span>
              </div>
              {livePassRate !== null && (
                <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
                  <div
                    className="h-full rounded-full bg-green-500"
                    style={{ width: `${livePassRate * 100}%` }}
                  />
                </div>
              )}
            </div>
            {/* Variety Score */}
            {run.variety_status && (
              <div className="border-t border-gray-100 pt-3">
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-1 text-sm text-gray-500">
                    <FiShuffle className="size-3.5" />
                    Variety Score
                  </span>
                  {run.variety_status === "running" ? (
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-blue-600">
                      <FiRefreshCw className="size-3 animate-spin" />
                      {run.variety_details?.progress
                        ? `${run.variety_details.progress.completed}/${run.variety_details.progress.total}`
                        : "Starting..."}
                    </span>
                  ) : run.variety_status === "failed" ? (
                    <span className="text-xs font-medium text-red-500">
                      Failed
                    </span>
                  ) : run.variety_status === "completed" &&
                    run.variety_score === null ? (
                    <span className="text-xs font-medium text-amber-600">
                      Generator unavailable
                    </span>
                  ) : (
                    <div className="flex items-center gap-2">
                      <span className="text-lg font-semibold text-gray-900">
                        {(run.variety_score * 100).toFixed(2) + "%"}
                      </span>
                      {run.is_static && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
                          <FiRepeat className="size-3" />
                          Static
                        </span>
                      )}
                    </div>
                  )}
                </div>
                {run.variety_status === "running" &&
                  run.variety_details?.progress && (
                    <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
                      <div
                        className="h-full rounded-full bg-blue-500 transition-all duration-500"
                        style={{
                          width: `${Math.round((run.variety_details.progress.completed / run.variety_details.progress.total) * 100)}%`,
                        }}
                      />
                    </div>
                  )}
                {run.variety_status === "completed" &&
                  run.variety_score !== null && (
                    <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
                      <div
                        className={`h-full rounded-full ${run.is_static ? "bg-red-400" : "bg-emerald-500"}`}
                        style={{
                          width: `${Math.min(run.variety_score * 100, 100)}%`,
                        }}
                      />
                    </div>
                  )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

RunDetailsSection.propTypes = {
  run: PropTypes.shape({
    status: PropTypes.string,
    total_items: PropTypes.number,
    items_generated: PropTypes.number,
    items_evaluated: PropTypes.number,
    filters: PropTypes.shape({
      grades: PropTypes.arrayOf(PropTypes.string),
      subjects: PropTypes.arrayOf(PropTypes.string),
      curriculum: PropTypes.string,
      request_category: PropTypes.string,
      has_interests: PropTypes.bool,
      has_stimulus: PropTypes.bool,
    }),
    created_at: PropTypes.string,
    started_at: PropTypes.string,
    completed_at: PropTypes.string,
    aggregate_score: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    pass_rate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    inceptbench_version: PropTypes.string,
    bm_generators: PropTypes.shape({
      name: PropTypes.string,
    }),
    raw_generation_results: PropTypes.object,
    raw_evaluation_results: PropTypes.object,
    error_message: PropTypes.string,
    variety_score: PropTypes.number,
    variety_status: PropTypes.string,
    is_static: PropTypes.bool,
    variety_details: PropTypes.object,
    skip_variety_check: PropTypes.bool,
  }).isRequired,
};

// Score Distribution Histogram Component
const PASS_THRESHOLD = 0.85;
const BIN_SIZE = 0.05; // 5% bins

const ScoreDistributionChart = ({ evaluationResults, generationResults }) => {
  const chartData = useMemo(() => {
    if (!evaluationResults || typeof evaluationResults !== "object") {
      return [];
    }

    // Extract scores using the same rules as aggregate score / pass rate:
    //   - Generation failures          → score 0 (counted in distribution)
    //   - Genuine evaluation errors    → excluded entirely
    //   - Empty content / valid evals  → use score (0 or actual)
    //   - Pending items (no eval yet, gen not failed) → skip
    const genResults = generationResults || {};

    let scores;
    if (Object.keys(genResults).length > 0) {
      scores = [];
      Object.keys(genResults).forEach((key) => {
        const gen = genResults[key];
        const evalResult = evaluationResults[key];

        // Generation failed → score 0 (covers both old runs with no eval entry
        // and new runs where the eval entry is { score: 0, error: null })
        if (gen?.success === false || gen?.error) {
          scores.push(0);
          return;
        }

        // No eval result yet — item is still pending (in-progress run), skip
        if (evalResult === undefined) {
          return;
        }

        // Genuine eval error → exclude entirely (not scored)
        if (evalResult.error) {
          return;
        }

        // Valid eval result (includes empty-content score: 0)
        scores.push(
          typeof evalResult.score === "number" && !isNaN(evalResult.score)
            ? evalResult.score
            : 0
        );
      });
    } else {
      // Fallback: no generation data available, use eval results directly (exclude eval errors)
      scores = Object.values(evaluationResults)
        .filter((result) => !result?.error)
        .map((result) => result?.score)
        .filter((score) => typeof score === "number" && !isNaN(score));
    }

    if (scores.length === 0) {
      return [];
    }

    // Create bins from 0 to 1 in 5% increments
    const bins = [];
    for (let i = 0; i < 1; i += BIN_SIZE) {
      const binStart = i;
      const binEnd = i + BIN_SIZE;
      const count = scores.filter(
        (score) => score >= binStart && score < binEnd
      ).length;
      // Handle the last bin to include 1.0
      const adjustedCount =
        binEnd >= 1
          ? scores.filter((score) => score >= binStart && score <= 1).length
          : count;

      bins.push({
        x: Math.round(binStart * 100) + 2.5, // Center of bin (offset by half bin width so bar spans correctly)
        count: adjustedCount,
        displayCount: adjustedCount === 0 ? 0.1 : adjustedCount, // Minimum height for empty bins
        binStart,
        binEnd: Math.min(binEnd, 1),
        isPassing: binStart >= PASS_THRESHOLD,
      });
    }

    return bins;
  }, [evaluationResults, generationResults]);

  if (chartData.length === 0) {
    return (
      <div className="flex items-center justify-center py-12 text-gray-500">
        No evaluation data available for visualization
      </div>
    );
  }

  // Custom bar shape with slight gap between bins
  const CustomBar = ({ x, y, width, height, fill }) => {
    const gap = 1; // 1px gap on each side
    return (
      <Rectangle
        x={x + gap}
        y={y}
        width={Math.max(width - gap * 2, 1)}
        height={height}
        fill={fill}
        radius={[2, 2, 0, 0]}
      />
    );
  };

  CustomBar.propTypes = {
    x: PropTypes.number,
    y: PropTypes.number,
    width: PropTypes.number,
    height: PropTypes.number,
    fill: PropTypes.string,
  };

  return (
    <div className="h-64">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart
          data={chartData}
          margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
          barGap={0}
          barCategoryGap={0}
        >
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="x"
            type="number"
            domain={[0, 100]}
            ticks={[0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100]}
            tick={{ fontSize: 11 }}
            tickFormatter={(value) => `${value}%`}
            allowDataOverflow={false}
          />
          <YAxis
            allowDecimals={false}
            tick={{ fontSize: 12 }}
            label={{
              value: "Count",
              angle: -90,
              position: "insideLeft",
              style: { fontSize: 12, fill: "#6b7280" },
            }}
          />
          <Tooltip
            content={({ active, payload }) => {
              if (active && payload && payload.length) {
                const data = payload[0].payload;
                return (
                  <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-lg">
                    <p className="text-sm font-medium text-gray-900">
                      {Math.round(data.binStart * 100)}% -{" "}
                      {Math.round(data.binEnd * 100)}%
                    </p>
                    <p className="text-sm text-gray-600">
                      Count: <span className="font-semibold">{data.count}</span>
                    </p>
                    <p
                      className={`text-xs ${data.isPassing ? "text-green-600" : "text-orange-600"}`}
                    >
                      {data.isPassing ? "✓ Passing" : "✗ Below threshold"}
                    </p>
                  </div>
                );
              }
              return null;
            }}
          />
          <ReferenceLine
            x={85}
            stroke="#6b7280"
            strokeDasharray="5 5"
            strokeWidth={2}
            label={{
              value: "Pass (85%)",
              position: "top",
              fill: "#6b7280",
              fontSize: 11,
            }}
          />
          <Bar dataKey="displayCount" shape={CustomBar}>
            {chartData.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={entry.isPassing ? "#22c55e" : "#f97316"}
              />
            ))}
          </Bar>
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

ScoreDistributionChart.propTypes = {
  evaluationResults: PropTypes.object,
  generationResults: PropTypes.object,
};

// Pass Rate Matrix Component
const PASS_RATE_DIFFICULTY_KEYS = ["easy", "medium", "hard"];
const PASS_RATE_KNOWN_TYPE_ORDER = [
  "mcq",
  "mcq_set",
  "saq",
  "leq",
  "dbq",
  "msq",
  "fill-in",
  "match",
  "article",
  "frq",
];
const PASS_RATE_TYPE_DISPLAY_LABELS = {
  mcq: "MCQ",
  mcq_set: "MCQ Set",
  saq: "SAQ",
  leq: "LEQ",
  dbq: "DBQ",
  msq: "MSQ",
  "fill-in": "Fill-In",
  frq: "FRQ",
};

const PassRateMatrix = ({ run }) => {
  const getTypeDisplayLabel = (type) => {
    if (PASS_RATE_TYPE_DISPLAY_LABELS[type]) {
      return PASS_RATE_TYPE_DISPLAY_LABELS[type];
    }
    return type
      .split("-")
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(" ");
  };

  const matrixData = useMemo(() => {
    if (!run || !run.raw_requests || !run.raw_evaluation_results) {
      return null;
    }

    const requests = run.raw_requests || {};
    const evaluations = run.raw_evaluation_results || {};

    const discoveredTypes = Array.from(
      new Set(
        Object.values(requests)
          .map((request) => request?.type?.toLowerCase())
          .filter(Boolean)
      )
    ).sort((a, b) => {
      const aKnownIndex = PASS_RATE_KNOWN_TYPE_ORDER.indexOf(a);
      const bKnownIndex = PASS_RATE_KNOWN_TYPE_ORDER.indexOf(b);
      const aRank = aKnownIndex === -1 ? Number.MAX_SAFE_INTEGER : aKnownIndex;
      const bRank = bKnownIndex === -1 ? Number.MAX_SAFE_INTEGER : bKnownIndex;

      if (aRank !== bRank) {
        return aRank - bRank;
      }

      return a.localeCompare(b);
    });

    // Initialize counters for each type/difficulty combination
    const stats = {};
    discoveredTypes.forEach((type) => {
      stats[type] = {};
      PASS_RATE_DIFFICULTY_KEYS.forEach((difficulty) => {
        stats[type][difficulty] = { total: 0, passed: 0 };
      });
    });

    // Process each request using the same rules as aggregate score / pass rate:
    //   - Generation failures          → total++ (score 0, never passed)
    //   - Genuine evaluation errors    → excluded (neither total nor passed)
    //   - Empty content / valid evals  → total++, passed if score >= 0.85
    //   - Pending items (no eval yet, gen not failed) → skip
    const generations = run?.raw_generation_results || {};
    Object.keys(requests).forEach((key) => {
      const request = requests[key];
      const generation = generations[key];
      const evaluation = evaluations[key];

      if (!request) {
        return;
      }

      const type = request.type?.toLowerCase();
      const difficulty = request.difficulty?.toLowerCase();

      if (
        !type ||
        !stats[type] ||
        !difficulty ||
        !PASS_RATE_DIFFICULTY_KEYS.includes(difficulty)
      ) {
        return;
      }

      // Generation failed or never generated → count in total (score 0, never passed)
      if (
        generation?.success === false ||
        generation?.error ||
        generation === undefined
      ) {
        stats[type][difficulty].total++;
        return;
      }

      // No eval result and gen didn't fail → item is still pending (in-progress run), skip
      if (evaluation === undefined) {
        return;
      }

      // Genuine eval error → exclude entirely (not counted)
      if (evaluation.error) {
        return;
      }

      // Valid eval result (includes empty-content score: 0)
      stats[type][difficulty].total++;
      const score = evaluation.score;
      if (typeof score === "number" && score >= 0.85) {
        stats[type][difficulty].passed++;
      }
    });

    // Calculate pass rates
    const matrix = {};
    Object.keys(stats).forEach((type) => {
      matrix[type] = {};
      Object.keys(stats[type]).forEach((difficulty) => {
        const { total, passed } = stats[type][difficulty];
        matrix[type][difficulty] = {
          total,
          passed,
          passRate: total > 0 ? (passed / total) * 100 : null,
        };
      });

      // Calculate overall for this type (across all difficulties)
      const typeTotal = Object.values(stats[type]).reduce(
        (acc, d) => acc + d.total,
        0
      );
      const typePassed = Object.values(stats[type]).reduce(
        (acc, d) => acc + d.passed,
        0
      );
      matrix[type].overall = {
        total: typeTotal,
        passed: typePassed,
        passRate: typeTotal > 0 ? (typePassed / typeTotal) * 100 : null,
      };
    });

    // Calculate overall row (across all types for each difficulty)
    matrix.overall = {};
    PASS_RATE_DIFFICULTY_KEYS.forEach((difficulty) => {
      const diffTotal = Object.keys(stats).reduce(
        (acc, type) => acc + stats[type][difficulty].total,
        0
      );
      const diffPassed = Object.keys(stats).reduce(
        (acc, type) => acc + stats[type][difficulty].passed,
        0
      );
      matrix.overall[difficulty] = {
        total: diffTotal,
        passed: diffPassed,
        passRate: diffTotal > 0 ? (diffPassed / diffTotal) * 100 : null,
      };
    });

    // Calculate grand total (all types, all difficulties)
    const grandTotal = Object.keys(stats).reduce(
      (acc, type) =>
        acc + Object.values(stats[type]).reduce((sum, d) => sum + d.total, 0),
      0
    );
    const grandPassed = Object.keys(stats).reduce(
      (acc, type) =>
        acc + Object.values(stats[type]).reduce((sum, d) => sum + d.passed, 0),
      0
    );
    matrix.overall.overall = {
      total: grandTotal,
      passed: grandPassed,
      passRate: grandTotal > 0 ? (grandPassed / grandTotal) * 100 : null,
    };

    return matrix;
  }, [run]);

  if (!matrixData) {
    return null;
  }

  // Check if there's any data
  const hasData = Object.values(matrixData).some((typeData) =>
    Object.values(typeData).some((diffData) => diffData.total > 0)
  );

  if (!hasData) {
    return null;
  }

  const getCellColor = (passRate) => {
    if (passRate === null) {
      return "bg-gray-200 text-gray-500";
    }
    // Round to match displayed value to avoid floating point issues
    const rounded = Math.round(passRate);
    if (rounded >= 99) {
      return "bg-green-200 text-green-800";
    }
    if (rounded >= 90) {
      return "bg-yellow-200 text-yellow-800";
    }
    return "bg-orange-200 text-orange-800";
  };

  const questionTypes = [
    ...Object.keys(matrixData)
      .filter((type) => type !== "overall")
      .map((type) => ({
        key: type,
        label: getTypeDisplayLabel(type),
      })),
    { key: "overall", label: "Overall" },
  ];

  const difficulties = [
    { key: "easy", label: "Easy" },
    { key: "medium", label: "Medium" },
    { key: "hard", label: "Hard" },
    { key: "overall", label: "Overall" },
  ];

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
      <h4 className="mb-4 text-sm font-medium text-gray-700">
        Pass Rate by Type & Difficulty
      </h4>

      <div className="overflow-x-auto">
        <div className="inline-block overflow-hidden rounded-lg border border-gray-200">
          {/* Header row */}
          <div className="flex">
            <div className="w-24 shrink-0 border-b border-r border-gray-200 bg-white px-3 py-2 text-left text-sm font-medium text-gray-700"></div>
            {difficulties.map((diff, idx) => (
              <div
                key={diff.key}
                className={`w-28 shrink-0 border-b border-gray-200 bg-white px-3 py-2 text-center text-sm font-medium text-gray-700 ${idx < difficulties.length - 1 ? "border-r" : ""}`}
              >
                {diff.label}
              </div>
            ))}
          </div>
          {/* Data rows */}
          {questionTypes.map((type, typeIdx) => (
            <div key={type.key} className="flex">
              <div
                className={`flex w-24 shrink-0 items-center border-r border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 ${typeIdx < questionTypes.length - 1 ? "border-b" : ""}`}
              >
                {type.label}
              </div>
              {difficulties.map((diff, diffIdx) => {
                const data = matrixData[type.key][diff.key];
                const passRate = data.passRate;

                return (
                  <div
                    key={diff.key}
                    title={
                      data.total > 0 && passRate !== null
                        ? `${passRate.toFixed(2)}% · ${data.passed}/${data.total}`
                        : undefined
                    }
                    className={`flex w-28 shrink-0 flex-col items-center justify-center p-3 ${getCellColor(passRate)} ${diffIdx < difficulties.length - 1 ? "border-r border-white/50" : ""} ${typeIdx < questionTypes.length - 1 ? "border-b border-white/50" : ""} ${data.total > 0 ? "cursor-help" : ""}`}
                  >
                    {data.total > 0 ? (
                      <>
                        <span className="text-base font-bold">
                          {passRate !== null ? `${passRate.toFixed(0)}%` : "-"}
                        </span>
                        <span className="text-xs opacity-75">
                          {data.passed}/{data.total}
                        </span>
                      </>
                    ) : (
                      <span className="text-sm">-</span>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      <div className="mt-3 flex items-center gap-4 text-xs text-gray-500">
        <span className="font-medium text-gray-600">Pass Rate:</span>
        <div className="flex items-center gap-1">
          <div className="size-3 rounded bg-green-200"></div>
          <span>99%+</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="size-3 rounded bg-yellow-200"></div>
          <span>90-98%</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="size-3 rounded bg-orange-200"></div>
          <span>&lt;90%</span>
        </div>
      </div>
    </div>
  );
};

PassRateMatrix.propTypes = {
  run: PropTypes.object,
};

// ============================================
// DIMENSION FOCUS AREAS COMPONENT
// ============================================
const DimensionFocusAreas = ({ run }) => {
  const [inFailedItemsOnly, setInFailedItemsOnly] = useState(false);

  const dimensionalData = useMemo(() => {
    if (!run || !run.raw_evaluation_results) {
      return null;
    }

    const evaluations = run.raw_evaluation_results || {};

    // Collect all dimensions and their pass/fail counts
    const dimensions = {};

    Object.values(evaluations).forEach((evalResult) => {
      if (!evalResult || evalResult.error || !evalResult.evaluation) {
        return;
      }

      const evaluation = evalResult.evaluation;

      // If toggle is on, only consider items that failed overall (score < 0.85)
      if (inFailedItemsOnly) {
        const overallScore = evaluation.overall?.score;
        if (typeof overallScore === "number" && overallScore >= 0.85) {
          return; // Skip items that passed overall
        }
      }

      // Iterate through all keys in the evaluation object
      Object.keys(evaluation).forEach((key) => {
        // Skip non-dimension fields
        if (
          [
            "overall",
            "content_type",
            "overall_rating",
            "subcontent_evaluations",
          ].includes(key)
        ) {
          return;
        }

        const dimension = evaluation[key];

        // Check if this looks like a dimension object with a score
        if (
          dimension &&
          typeof dimension === "object" &&
          typeof dimension.score === "number"
        ) {
          if (!dimensions[key]) {
            dimensions[key] = { total: 0, passed: 0, failed: 0 };
          }

          dimensions[key].total++;
          if (dimension.score >= 1) {
            dimensions[key].passed++;
          } else {
            dimensions[key].failed++;
          }
        }
      });
    });

    // Convert to array and sort by failures (most failures first)
    const result = Object.entries(dimensions)
      .map(([name, data]) => ({
        name,
        displayName: name
          .split("_")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" "),
        total: data.total,
        passed: data.passed,
        failed: data.failed,
      }))
      .filter((d) => d.total > 0)
      .sort((a, b) => b.failed - a.failed); // Sort by failures descending (most failures first)

    return result;
  }, [run, inFailedItemsOnly]);

  // Count total items and failed items
  const itemCounts = useMemo(() => {
    if (!run || !run.raw_evaluation_results) {
      return { total: 0, failed: 0 };
    }
    const evaluations = run.raw_evaluation_results || {};
    let total = 0;
    let failed = 0;

    Object.values(evaluations).forEach((evalResult) => {
      if (!evalResult || evalResult.error || !evalResult.evaluation) {
        return;
      }
      total++;
      const overallScore = evalResult.evaluation.overall?.score;
      if (typeof overallScore === "number" && overallScore < 0.85) {
        failed++;
      }
    });

    return { total, failed };
  }, [run]);

  if (!dimensionalData || dimensionalData.length === 0) {
    return null;
  }

  // Count total failures and dimensions with failures
  const totalFailures = dimensionalData.reduce((sum, d) => sum + d.failed, 0);
  const dimensionsWithFailures = dimensionalData.filter(
    (d) => d.failed > 0
  ).length;

  return (
    <div className="flex max-h-[400px] flex-col rounded-lg border border-gray-200 bg-gray-50 p-4">
      <div className="mb-2 flex shrink-0 items-center justify-between">
        <h4 className="text-sm font-medium text-gray-700">
          Dimension Focus Areas
        </h4>
        <label className="flex cursor-pointer items-center gap-2">
          <span className="text-xs text-gray-500">In failed items</span>
          <div className="relative">
            <input
              type="checkbox"
              checked={inFailedItemsOnly}
              onChange={(e) => setInFailedItemsOnly(e.target.checked)}
              className="peer sr-only"
            />
            <div className="h-5 w-9 rounded-full bg-gray-300 transition-colors peer-checked:bg-primary-500"></div>
            <div className="absolute left-0.5 top-0.5 size-4 rounded-full bg-white shadow-sm transition-transform peer-checked:translate-x-4"></div>
          </div>
        </label>
      </div>

      {/* Contextual description */}
      <p className="mb-3 shrink-0 text-xs text-gray-500">
        {inFailedItemsOnly
          ? `In the ${itemCounts.failed} items that failed overall, these are the dimensions that caused the failures`
          : `Across all ${itemCounts.total} items, these dimensions failed the most`}
      </p>

      {/* Summary */}
      {totalFailures > 0 ? (
        <div className="mb-3 shrink-0 rounded-md bg-orange-50 px-3 py-2 text-xs text-orange-700">
          <span className="font-semibold">{dimensionsWithFailures}</span>{" "}
          dimension{dimensionsWithFailures !== 1 ? "s" : ""} with failures
        </div>
      ) : (
        <div className="mb-3 shrink-0 rounded-md bg-green-50 px-3 py-2 text-xs text-green-700">
          <FiCheck className="mr-1 inline size-3" />
          All dimensions passed in{" "}
          {inFailedItemsOnly ? "failed items" : "all items"}
        </div>
      )}

      <div className="min-h-0 flex-1 space-y-1.5 overflow-y-auto pr-2">
        {dimensionalData.map((dim) => (
          <div
            key={dim.name}
            className="flex items-center gap-3 rounded-md bg-white p-2"
          >
            <div className="min-w-0 flex-1">
              <span className="text-sm text-gray-700">{dim.displayName}</span>
            </div>
            <div className="flex items-center gap-2">
              {dim.failed > 0 ? (
                <>
                  <span className="text-xs text-gray-400">
                    (out of {dim.total})
                  </span>
                  <div className="min-w-[80px] rounded bg-orange-100 px-2 py-1 text-center text-sm font-semibold text-orange-700">
                    {dim.failed} failed
                  </div>
                </>
              ) : (
                <div className="flex min-w-[80px] items-center justify-center gap-1 rounded bg-green-100 px-2 py-1 text-center text-sm font-medium text-green-700">
                  <FiCheck className="size-3.5" />
                  <span>All passed</span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

DimensionFocusAreas.propTypes = {
  run: PropTypes.object,
};

// ============================================
// ERROR GROUPING UTILITIES
// ============================================

// Normalize error messages by replacing dynamic parts with placeholders
const normalizeErrorMessage = (errorMessage) => {
  if (!errorMessage || typeof errorMessage !== "string") {
    return String(errorMessage || "Unknown error");
  }

  let normalized = errorMessage;

  // Replace UUIDs (e.g., 2a4bfa00-0e19-42e8-abdf-e3542deb959c)
  normalized = normalized.replace(
    /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi,
    "<UUID>"
  );

  // Replace question IDs (e.g., q_CCSS.MATH.CONTENT.3.OA.D.8+7_easy_1768228835)
  normalized = normalized.replace(/q_[A-Za-z0-9.+_-]+_\d+/g, "<QUESTION_ID>");

  // Replace timestamps (long numeric strings, 10+ digits)
  normalized = normalized.replace(/\b\d{10,}\b/g, "<TIMESTAMP>");

  // Replace HTTP URLs
  normalized = normalized.replace(/https?:\/\/[^\s"']+/gi, "<URL>");

  // Replace IP addresses
  normalized = normalized.replace(
    /\b\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}(:\d+)?\b/g,
    "<IP_ADDRESS>"
  );

  // Replace port numbers in context
  normalized = normalized.replace(/port\s*:?\s*\d+/gi, "port <PORT>");

  // Replace file paths (common patterns)
  normalized = normalized.replace(
    /\/[a-zA-Z0-9_\-./]+\.(js|ts|py|json|svg|png|jpg|jpeg|gif)/gi,
    "<FILE_PATH>"
  );

  // Replace line/column numbers in stack traces
  normalized = normalized.replace(/:\d+:\d+/g, ":<LINE>:<COL>");

  // Replace standalone large numbers that might be IDs
  normalized = normalized.replace(/\b\d{6,9}\b/g, "<ID>");

  return normalized;
};

// Extract error message from various error formats
const extractErrorMessage = (error) => {
  if (!error) {
    return null;
  }
  if (typeof error === "string") {
    return error;
  }
  if (error.message) {
    return error.message;
  }
  if (error.detail) {
    return error.detail;
  }
  if (error.error) {
    return extractErrorMessage(error.error);
  }
  return JSON.stringify(error);
};

const SCHEMA_VALIDATION_ERROR_PREFIX = "schema validation failed:";

const isSchemaValidationError = (message) =>
  typeof message === "string" &&
  message.toLowerCase().startsWith(SCHEMA_VALIDATION_ERROR_PREFIX);

// Group errors by normalized pattern
const groupErrors = (errors) => {
  const groups = {};

  errors.forEach(({ key, error }) => {
    const message = extractErrorMessage(error);
    if (!message) {
      return;
    }

    const normalizedMessage = normalizeErrorMessage(message);

    if (!groups[normalizedMessage]) {
      groups[normalizedMessage] = {
        pattern: normalizedMessage,
        count: 0,
        examples: [],
        keys: [],
        isSchemaValidation: false,
      };
    }

    groups[normalizedMessage].count++;
    groups[normalizedMessage].keys.push(key);
    groups[normalizedMessage].isSchemaValidation =
      groups[normalizedMessage].isSchemaValidation ||
      isSchemaValidationError(message);

    // Keep up to 3 unique examples
    if (groups[normalizedMessage].examples.length < 3) {
      const exampleExists = groups[normalizedMessage].examples.some(
        (ex) => ex === message
      );
      if (!exampleExists) {
        groups[normalizedMessage].examples.push(message);
      }
    }
  });

  // Sort by count descending
  return Object.values(groups).sort((a, b) => b.count - a.count);
};

// Errors Modal Component
const ErrorsModal = ({ generationErrors, evaluationErrors, onClose }) => {
  const [activeTab, setActiveTab] = useState("generation");
  const [expandedGroup, setExpandedGroup] = useState(null);

  const groupedGenerationErrors = useMemo(
    () => groupErrors(generationErrors),
    [generationErrors]
  );
  const groupedEvaluationErrors = useMemo(
    () => groupErrors(evaluationErrors),
    [evaluationErrors]
  );

  const currentErrors =
    activeTab === "generation"
      ? groupedGenerationErrors
      : groupedEvaluationErrors;
  const currentCount =
    activeTab === "generation"
      ? generationErrors.length
      : evaluationErrors.length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="flex h-[80vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        {/* Header */}
        <div className="flex shrink-0 items-center justify-between border-b border-gray-200 px-6 py-4">
          <div className="flex items-center gap-3">
            <h3 className="text-lg font-semibold text-gray-900">
              Error Analysis
            </h3>
            <span
              className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
                currentCount > 0
                  ? "bg-red-100 text-red-700"
                  : "bg-green-100 text-green-700"
              }`}
            >
              {currentCount} errors
            </span>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
          >
            <FiX className="size-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex shrink-0 border-b border-gray-200 bg-gray-50">
          <button
            onClick={() => setActiveTab("generation")}
            className={`flex items-center gap-2 px-6 py-3 text-sm font-medium transition-colors ${
              activeTab === "generation"
                ? "border-b-2 border-primary-600 bg-white text-primary-600"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            Generation Errors
            <span
              className={`rounded-full px-2 py-0.5 text-xs ${
                activeTab === "generation"
                  ? "bg-primary-100 text-primary-700"
                  : "bg-gray-200 text-gray-600"
              }`}
            >
              {generationErrors.length}
            </span>
          </button>
          <button
            onClick={() => setActiveTab("evaluation")}
            className={`flex items-center gap-2 px-6 py-3 text-sm font-medium transition-colors ${
              activeTab === "evaluation"
                ? "border-b-2 border-primary-600 bg-white text-primary-600"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            Evaluation Errors
            <span
              className={`rounded-full px-2 py-0.5 text-xs ${
                activeTab === "evaluation"
                  ? "bg-primary-100 text-primary-700"
                  : "bg-gray-200 text-gray-600"
              }`}
            >
              {evaluationErrors.length}
            </span>
          </button>
        </div>

        {/* Content */}
        <div className="min-h-0 flex-1 overflow-y-auto p-6">
          {currentErrors.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <FiCheckCircle className="mb-3 size-12 text-green-400" />
              <p className="font-medium text-gray-700">No errors found</p>
              <p className="text-sm text-gray-500">
                All {activeTab === "generation" ? "generations" : "evaluations"}{" "}
                completed successfully
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {currentErrors.map((group, idx) => (
                <div
                  key={idx}
                  className="overflow-hidden rounded-xl border border-gray-200 bg-white"
                >
                  {/* Error Group Header */}
                  <button
                    onClick={() =>
                      setExpandedGroup(expandedGroup === idx ? null : idx)
                    }
                    className="flex w-full items-start justify-between gap-4 p-4 text-left transition-colors hover:bg-gray-50"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <FiAlertCircle className="size-4 shrink-0 text-red-500" />
                        {activeTab === "generation" &&
                          group.isSchemaValidation && (
                            <span className="rounded-full bg-orange-100 px-2 py-0.5 text-xs font-semibold text-orange-700">
                              Schema Validation
                            </span>
                          )}
                        <span className="font-mono text-sm text-gray-900">
                          {group.pattern.length > 150
                            ? group.pattern.slice(0, 150) + "..."
                            : group.pattern}
                        </span>
                      </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-3">
                      <span className="rounded-full bg-red-100 px-3 py-1 text-sm font-bold text-red-700">
                        ×{group.count}
                      </span>
                      <FiChevronRight
                        className={`size-5 text-gray-400 transition-transform ${
                          expandedGroup === idx ? "rotate-90" : ""
                        }`}
                      />
                    </div>
                  </button>

                  {/* Expanded Content */}
                  {expandedGroup === idx && (
                    <div className="border-t border-gray-100 bg-gray-50 p-4">
                      {activeTab === "generation" &&
                        group.isSchemaValidation && (
                          <div className="mb-3 rounded-lg border border-orange-200 bg-orange-50 p-3">
                            <p className="text-xs font-medium text-orange-800">
                              Schema validation failed for these items. Fix the
                              generator response structure and rerun to evaluate
                              again.
                            </p>
                          </div>
                        )}

                      {/* Example Errors */}
                      <div className="mb-3">
                        <h5 className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
                          Example{group.examples.length > 1 ? "s" : ""} (
                          {group.examples.length} of {group.count})
                        </h5>
                        <div className="space-y-2">
                          {group.examples.map((example, exIdx) => (
                            <div
                              key={exIdx}
                              className="rounded-lg border border-red-200 bg-red-50 p-3"
                            >
                              <p className="break-words font-mono text-xs text-red-800">
                                {example}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Affected Keys */}
                      <div>
                        <h5 className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
                          Affected Items ({group.keys.length})
                        </h5>
                        <div className="flex flex-wrap gap-1">
                          {group.keys.slice(0, 10).map((key, keyIdx) => (
                            <span
                              key={keyIdx}
                              className="rounded bg-gray-200 px-2 py-0.5 font-mono text-xs text-gray-600"
                            >
                              {key.length > 30 ? key.slice(0, 30) + "..." : key}
                            </span>
                          ))}
                          {group.keys.length > 10 && (
                            <span className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-500">
                              +{group.keys.length - 10} more
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

ErrorsModal.propTypes = {
  generationErrors: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      error: PropTypes.any,
    })
  ).isRequired,
  evaluationErrors: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      error: PropTypes.any,
    })
  ).isRequired,
  onClose: PropTypes.func.isRequired,
};

// Errors Insight Component
const ErrorsInsight = ({ run, onOpenModal, isInProgress }) => {
  const { generationErrors, evaluationErrors } = useMemo(() => {
    const genErrors = [];
    const evalErrors = [];

    const requests = run?.raw_requests || {};
    const generations = run?.raw_generation_results || {};
    const evaluations = run?.raw_evaluation_results || {};

    // Extract generation errors from existing results
    Object.entries(generations).forEach(([key, value]) => {
      if (value?.error) {
        genErrors.push({ key, error: value.error });
      }
    });

    // Detect items in raw_requests that were never generated
    if (!isInProgress) {
      Object.keys(requests).forEach((key) => {
        if (!(key in generations)) {
          genErrors.push({ key, error: "Never generated" });
        }
      });
    }

    // Extract evaluation errors
    Object.keys(generations).forEach((key) => {
      const gen = generations[key];
      if (!gen?.error && !evaluations[key]) {
        if (!isInProgress) {
          evalErrors.push({
            key,
            error: "Item was generated but not evaluated",
          });
        }
      } else if (evaluations[key]?.error) {
        evalErrors.push({ key, error: evaluations[key].error });
      }
    });

    return { generationErrors: genErrors, evaluationErrors: evalErrors };
  }, [run, isInProgress]);

  const totalErrors = generationErrors.length + evaluationErrors.length;

  if (totalErrors === 0) {
    return null;
  }

  return (
    <button
      onClick={() => onOpenModal(generationErrors, evaluationErrors)}
      className="flex size-full flex-col justify-center rounded-lg border border-red-200 bg-red-50 p-4 text-left transition-colors hover:border-red-300 hover:bg-red-100"
    >
      <div className="flex flex-col items-center gap-3 text-center">
        <div className="flex size-12 items-center justify-center rounded-full bg-red-100">
          <FiAlertCircle className="size-6 text-red-600" />
        </div>
        <div>
          <h4 className="font-semibold text-red-900">Errors Detected</h4>
          <p className="mt-1 text-sm text-red-700">
            {generationErrors.length} generation
          </p>
          <p className="text-sm text-red-700">
            {evaluationErrors.length} evaluation
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="rounded-full bg-red-200 px-4 py-1.5 text-xl font-bold text-red-800">
            {totalErrors}
          </span>
        </div>
        <span className="flex items-center gap-1 text-xs font-medium text-red-600">
          Click to view details
          <FiChevronRight className="size-4" />
        </span>
      </div>
    </button>
  );
};

ErrorsInsight.propTypes = {
  run: PropTypes.object,
  onOpenModal: PropTypes.func.isRequired,
  isInProgress: PropTypes.bool,
};

// Insights Section Component
const InsightsSection = ({ run }) => {
  const [showErrorsModal, setShowErrorsModal] = useState(false);
  const [errorsData, setErrorsData] = useState({
    generationErrors: [],
    evaluationErrors: [],
  });

  const isInProgress = ["generating", "evaluating", "queued"].includes(
    run?.status
  );

  const hasEvaluationData =
    run?.raw_evaluation_results &&
    typeof run.raw_evaluation_results === "object" &&
    Object.keys(run.raw_evaluation_results).length > 0;

  // Check if there are any errors
  const hasErrors = useMemo(() => {
    const requests = run?.raw_requests || {};
    const generations = run?.raw_generation_results || {};
    const evaluations = run?.raw_evaluation_results || {};

    // Check for items in raw_requests that were never generated
    if (!isInProgress) {
      const hasNeverGenerated = Object.keys(requests).some(
        (key) => !(key in generations)
      );
      if (hasNeverGenerated) {
        return true;
      }
    }

    // Check for generation errors
    const hasGenErrors = Object.values(generations).some((g) => g?.error);

    // Check for evaluation errors (generated but not evaluated, or eval error)
    const hasEvalErrors = Object.keys(generations).some((key) => {
      const gen = generations[key];
      if (gen?.error) {
        return false;
      }
      if (!evaluations[key]) {
        return !isInProgress;
      }
      return evaluations[key]?.error;
    });

    return hasGenErrors || hasEvalErrors;
  }, [run, isInProgress]);

  const handleOpenErrorsModal = (genErrors, evalErrors) => {
    setErrorsData({
      generationErrors: genErrors,
      evaluationErrors: evalErrors,
    });
    setShowErrorsModal(true);
  };

  if (!hasEvaluationData && !hasErrors) {
    return null;
  }

  return (
    <>
      <div className="border-b border-gray-200 bg-white px-6 py-5">
        <div className="mb-4 flex items-center gap-2">
          <h3 className="text-lg font-semibold text-gray-900">Insights</h3>
          <span className="text-lg">💡</span>
        </div>

        <div className="mx-auto max-w-6xl space-y-4">
          {/* First Row: Score Distribution (Left) and Errors (Right) */}
          <div className="flex flex-col gap-4 lg:flex-row">
            {/* Left: Score Distribution Chart */}
            {hasEvaluationData && (
              <div className="min-w-0 flex-1">
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                  <h4 className="mb-2 text-sm font-medium text-gray-700">
                    Score Distribution
                  </h4>
                  <p className="mb-4 text-xs text-gray-500">
                    Distribution of evaluation scores.{" "}
                    <span className="text-green-600">Green</span> = passing
                    (≥85%), <span className="text-orange-500">Orange</span> =
                    below threshold.
                  </p>
                  <ScoreDistributionChart
                    evaluationResults={run?.raw_evaluation_results}
                    generationResults={run?.raw_generation_results}
                  />
                </div>
              </div>
            )}

            {/* Right: Errors Insight */}
            {hasErrors && (
              <div className="lg:w-64 lg:shrink-0">
                <ErrorsInsight
                  run={run}
                  onOpenModal={handleOpenErrorsModal}
                  isInProgress={isInProgress}
                />
              </div>
            )}
          </div>

          {/* Second Row: Pass Rate Matrix (Left) and Dimensional Pass Rate (Right) */}
          <div className="flex flex-col gap-4 lg:flex-row lg:items-stretch">
            {/* Left: Pass Rate Matrix */}
            {hasEvaluationData && (
              <div className="shrink-0">
                <PassRateMatrix run={run} />
              </div>
            )}

            {/* Right: Dimensional Pass Rate */}
            {hasEvaluationData && (
              <div className="min-h-0 min-w-0 flex-1">
                <DimensionFocusAreas run={run} />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Errors Modal */}
      {showErrorsModal && (
        <ErrorsModal
          generationErrors={errorsData.generationErrors}
          evaluationErrors={errorsData.evaluationErrors}
          onClose={() => setShowErrorsModal(false)}
        />
      )}
    </>
  );
};

InsightsSection.propTypes = {
  run: PropTypes.object,
};

// ============================================
// UNIFIED VIEW COMPONENTS
// ============================================

// JSON View with Copy Button
const JsonView = ({ data, onCopy, copied }) => (
  <div className="relative">
    <button
      onClick={onCopy}
      className="absolute right-2 top-2 rounded p-1.5 text-gray-400 transition-colors hover:bg-gray-200 hover:text-gray-600"
      title="Copy JSON"
    >
      {copied ? (
        <FiCheck className="size-4 text-green-500" />
      ) : (
        <FiCopy className="size-4" />
      )}
    </button>
    <pre className="max-h-96 overflow-auto rounded-lg bg-gray-50 p-4 pr-10 font-mono text-xs text-gray-700">
      {JSON.stringify(data, null, 2)}
    </pre>
  </div>
);

JsonView.propTypes = {
  data: PropTypes.any,
  onCopy: PropTypes.func.isRequired,
  copied: PropTypes.bool,
};

// JSON Modal for expanded view
const JsonModal = ({ title, data, onClose }) => {
  const [copied, setCopied] = useState(false);

  const copyJson = () => {
    navigator.clipboard.writeText(JSON.stringify(data, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="max-h-[90vh] w-full max-w-4xl overflow-hidden rounded-2xl bg-white shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <div className="flex items-center gap-2">
            <button
              onClick={copyJson}
              className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-100"
              title="Copy JSON"
            >
              {copied ? (
                <>
                  <FiCheck className="size-4 text-green-500" />
                  Copied!
                </>
              ) : (
                <>
                  <FiCopy className="size-4" />
                  Copy
                </>
              )}
            </button>
            <button
              onClick={onClose}
              className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
            >
              <FiX className="size-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="max-h-[calc(90vh-80px)] overflow-auto p-6">
          <pre className="rounded-lg bg-gray-50 p-4 font-mono text-sm text-gray-700">
            {JSON.stringify(data, null, 2)}
          </pre>
        </div>
      </div>
    </div>
  );
};

JsonModal.propTypes = {
  title: PropTypes.string.isRequired,
  data: PropTypes.any,
  onClose: PropTypes.func.isRequired,
};

// Request Column - Clean display of request data
const RequestColumn = ({ request, curriculumMetadata, curriculum }) => {
  const [showCurriculumModal, setShowCurriculumModal] = useState(false);
  const [activeTab, setActiveTab] = useState(null);
  const [metadataViewMode, setMetadataViewMode] = useState("ui"); // "ui" or "json"
  const [metadataCopied, setMetadataCopied] = useState(false);
  const [liveCurriculum, setLiveCurriculum] = useState(null);
  const [liveCurriculumLoading, setLiveCurriculumLoading] = useState(false);

  // Fetch the rich Incept Curriculum data for this substandard so the
  // metadata modal can surface fields the bm_runs JSONB doesn't store
  // (key_concepts, lesson_name, full per-level difficulty_definitions,
  // parent standard, domain/cluster ancestry).
  const substandardId = request?.skills?.substandard_id;
  const requestSubject = request?.subject;
  const requestGrade = request?.grade;
  const requestVersion = request?.curriculum_version;
  useEffect(() => {
    if (!substandardId) {
      setLiveCurriculum(null);
      setLiveCurriculumLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLiveCurriculumLoading(true);
    fetchCurriculumItem({
      itemId: substandardId,
      curriculum,
      subject: requestSubject,
      course: requestGrade,
      version: requestVersion,
    })
      .then(({ data }) => {
        if (cancelled) {
          return;
        }
        setLiveCurriculum(data);
        setLiveCurriculumLoading(false);
      })
      .catch(() => {
        if (cancelled) {
          return;
        }
        setLiveCurriculum(null);
        setLiveCurriculumLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [substandardId, curriculum, requestSubject, requestGrade, requestVersion]);

  const formatDisplayValue = (value) => {
    if (typeof value === "string") {
      return value;
    }
    if (typeof value === "number" || typeof value === "boolean") {
      return String(value);
    }
    if (Array.isArray(value)) {
      return value.map((item) => formatDisplayValue(item)).join(", ");
    }
    if (value && typeof value === "object") {
      return Object.entries(value)
        .map(
          ([key, nestedValue]) => `${key}: ${formatDisplayValue(nestedValue)}`
        )
        .join(" | ");
    }
    return "";
  };

  if (!request) {
    return <div className="text-sm italic text-gray-400">No request data</div>;
  }

  // Prefer rich live API attributes when available; fall back to the
  // flattened bm_runs.curriculum_metadata.
  const liveAttrs = liveCurriculum?.item?.attributes || null;
  const ancestry = liveCurriculum?.ancestry || null;
  const formatDifficultyDefinitions = (defs) =>
    defs && typeof defs === "object"
      ? ["easy", "medium", "hard"]
          .filter((lvl) => defs[lvl])
          .map(
            (lvl) =>
              `* ${lvl.charAt(0).toUpperCase() + lvl.slice(1)}:\n${defs[lvl]}`
          )
          .join("\n\n")
      : null;
  const formatAncestry = (a) =>
    a
      ? [
          a.subject?.name && `Subject: ${a.subject.name}`,
          a.course?.name && `Course: ${a.course.name}`,
          a.domain?.domain_name && `Domain: ${a.domain.domain_name}`,
          a.cluster?.cluster_name && `Cluster: ${a.cluster.cluster_name}`,
          a.standard?.standard_id &&
            `Parent standard: ${a.standard.standard_id}`,
          a.standard?.standard_description &&
            `Parent description: ${a.standard.standard_description}`,
        ]
          .filter(Boolean)
          .join("\n")
      : null;

  // Define metadata tabs configuration. Live values from the curriculum API
  // override stored ones because they're authoritative (and richer).
  const metadataTabs = [
    {
      id: "lessonName",
      label: "Lesson Name",
      shortLabel: "Lesson",
      icon: "📘",
      content: liveAttrs?.lesson_name || null,
      colors: {
        bg: "bg-indigo-50",
        bgHover: "hover:bg-indigo-100",
        border: "border-indigo-300",
        borderIdle: "border-indigo-200",
        text: "text-indigo-800",
        textLight: "text-indigo-700",
        textMuted: "text-indigo-600",
      },
      isList: false,
    },
    {
      id: "keyConcepts",
      label: "Key Concepts",
      shortLabel: "Concepts",
      icon: "💡",
      content: liveAttrs?.key_concepts || null,
      colors: {
        bg: "bg-pink-50",
        bgHover: "hover:bg-pink-100",
        border: "border-pink-300",
        borderIdle: "border-pink-200",
        text: "text-pink-800",
        textLight: "text-pink-700",
        textMuted: "text-pink-600",
      },
      isList: true,
    },
    {
      id: "learningObjectives",
      label: "Learning Objectives",
      shortLabel: "Objectives",
      icon: "🎯",
      content:
        liveAttrs?.learning_objectives ||
        curriculumMetadata?.learning_objectives,
      colors: {
        bg: "bg-blue-50",
        bgHover: "hover:bg-blue-100",
        border: "border-blue-300",
        borderIdle: "border-blue-200",
        text: "text-blue-800",
        textLight: "text-blue-700",
        textMuted: "text-blue-600",
      },
      isList: !!liveAttrs?.learning_objectives,
    },
    {
      id: "assessmentBoundaries",
      label: "Assessment Boundaries",
      shortLabel: "Boundaries",
      icon: "📏",
      content:
        liveAttrs?.assessment_boundaries ||
        curriculumMetadata?.assessment_boundaries,
      colors: {
        bg: "bg-purple-50",
        bgHover: "hover:bg-purple-100",
        border: "border-purple-300",
        borderIdle: "border-purple-200",
        text: "text-purple-800",
        textLight: "text-purple-700",
        textMuted: "text-purple-600",
      },
      isList: !!liveAttrs?.assessment_boundaries,
    },
    {
      id: "commonMisconceptions",
      label: "Common Misconceptions",
      shortLabel: "Misconceptions",
      icon: "⚠️",
      content:
        liveAttrs?.common_misconceptions ||
        curriculumMetadata?.common_misconceptions,
      colors: {
        bg: "bg-amber-50",
        bgHover: "hover:bg-amber-100",
        border: "border-amber-300",
        borderIdle: "border-amber-200",
        text: "text-amber-800",
        textLight: "text-amber-700",
        textMuted: "text-amber-600",
      },
      isList: !!liveAttrs?.common_misconceptions,
    },
    {
      id: "difficultyDefinition",
      label: "Difficulty Definitions",
      shortLabel: "Difficulty",
      icon: "📊",
      content:
        formatDifficultyDefinitions(liveAttrs?.difficulty_definitions) ||
        curriculumMetadata?.difficulty_definition,
      colors: {
        bg: "bg-green-50",
        bgHover: "hover:bg-green-100",
        border: "border-green-300",
        borderIdle: "border-green-200",
        text: "text-green-800",
        textLight: "text-green-700",
        textMuted: "text-green-600",
      },
      isList:
        !liveAttrs?.difficulty_definitions &&
        Array.isArray(curriculumMetadata?.difficulty_definition),
    },
    {
      id: "ancestry",
      label: "Standard Ancestry",
      shortLabel: "Ancestry",
      icon: "🗂️",
      content: formatAncestry(ancestry),
      colors: {
        bg: "bg-slate-50",
        bgHover: "hover:bg-slate-100",
        border: "border-slate-300",
        borderIdle: "border-slate-200",
        text: "text-slate-800",
        textLight: "text-slate-700",
        textMuted: "text-slate-600",
      },
      isList: false,
    },
  ];

  // Filter to only show tabs with content
  const availableTabs = metadataTabs.filter(
    (tab) =>
      tab.content &&
      (Array.isArray(tab.content) ? tab.content.length > 0 : true)
  );

  const openCurriculumModal = () => {
    if (availableTabs.length > 0) {
      setActiveTab(availableTabs[0].id);
      setShowCurriculumModal(true);
    }
  };

  const closeCurriculumModal = () => {
    setShowCurriculumModal(false);
    setActiveTab(null);
    setMetadataViewMode("ui");
  };

  const currentTab = availableTabs.find((tab) => tab.id === activeTab);

  // Helper to render structured content with bullet points
  const renderStructuredContent = (content, colors) => {
    const normalizedContent =
      typeof content === "string" ? content : formatDisplayValue(content);
    if (!normalizedContent) {
      return null;
    }

    const lines = normalizedContent.split("\n").filter((line) => line.trim());
    let heading = null;
    const bulletPoints = [];

    lines.forEach((line) => {
      const trimmed = line.trim();

      // Check if it's a main heading (starts with *)
      if (trimmed.startsWith("* ")) {
        if (!heading) {
          heading = trimmed.substring(2);
        } else {
          bulletPoints.push(trimmed.substring(2));
        }
      }
      // Check if it's a bullet point (starts with -)
      else if (trimmed.startsWith("- ")) {
        bulletPoints.push(trimmed.substring(2));
      }
      // Plain text line - first one becomes heading, rest become bullets
      else if (trimmed) {
        if (!heading) {
          heading = trimmed;
        } else {
          bulletPoints.push(trimmed);
        }
      }
    });

    // If no content found, render as plain text
    if (!heading && bulletPoints.length === 0) {
      return (
        <div className="whitespace-pre-wrap text-sm leading-relaxed text-gray-700">
          {normalizedContent}
        </div>
      );
    }

    return (
      <div
        className={`rounded-lg border ${colors.borderIdle} ${colors.bg} p-4`}
      >
        {/* Heading */}
        {heading && (
          <div className={`mb-3 font-medium ${colors.text} text-sm`}>
            {heading}
          </div>
        )}

        {/* Bullet points */}
        {bulletPoints.length > 0 && (
          <ul className="space-y-2">
            {bulletPoints.map((point, idx) => (
              <li key={idx} className="flex items-start gap-2">
                <span
                  className={`mt-1.5 size-1.5 shrink-0 rounded-full ${colors.border} border bg-white`}
                />
                <span className="text-sm leading-relaxed text-gray-700">
                  {point}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  };

  const gradeLabel = curriculum?.toLowerCase() === "ap" ? "Unit" : "Grade";
  const fields = [
    { key: "grade", label: gradeLabel, icon: "📚" },
    { key: "subject", label: "Subject", icon: "📖" },
    { key: "type", label: "Type", icon: "📝" },
    { key: "difficulty", label: "Difficulty", icon: "⚡" },
    { key: "locale", label: "Locale", icon: "🌍" },
  ];

  const getDifficultyColor = (difficulty) => {
    switch (formatDisplayValue(difficulty).toLowerCase()) {
      case "easy":
        return "bg-green-100 text-green-700";
      case "medium":
        return "bg-yellow-100 text-yellow-700";
      case "hard":
        return "bg-red-100 text-red-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        {fields.map(({ key, label, icon }) => {
          const value = request[key];
          if (!value) {
            return null;
          }
          const displayValue = formatDisplayValue(value);

          const isType = key === "type";
          const isDifficulty = key === "difficulty";

          return (
            <div
              key={key}
              className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${
                isDifficulty
                  ? getDifficultyColor(value)
                  : isType
                    ? "bg-primary-100 text-primary-700"
                    : "bg-gray-100 text-gray-700"
              }`}
              title={label}
            >
              <span>{icon}</span>
              <span className="capitalize">{displayValue}</span>
            </div>
          );
        })}
      </div>

      {/* Skills */}
      {request.skills && (
        <div className="rounded-lg bg-gray-50 p-3">
          <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
            Skills
          </div>
          {request.skills.lesson_title && (
            <p className="text-sm text-gray-900">
              {formatDisplayValue(request.skills.lesson_title)}
            </p>
          )}
          {request.skills.substandard_id && (
            <p className="mt-1 font-mono text-xs text-gray-500">
              {formatDisplayValue(request.skills.substandard_id)}
            </p>
          )}
          {request.skills.substandard_description && (
            <p className="mt-1 text-xs text-gray-600">
              {formatDisplayValue(request.skills.substandard_description)}
            </p>
          )}
        </div>
      )}

      {/* Curriculum Metadata Button — also shown while live data is loading
          so reviewers on older runs (no cached curriculum_metadata) don't
          see the button pop in only after the network round-trip. */}
      {(availableTabs.length > 0 ||
        (liveCurriculumLoading && substandardId)) && (
        <>
          <div className="mt-4">
            <button
              onClick={openCurriculumModal}
              disabled={availableTabs.length === 0}
              className="group flex w-full items-center gap-2 rounded-lg border border-indigo-200 bg-indigo-50 p-2.5 text-left transition-all hover:border-indigo-300 hover:shadow-md active:scale-[0.98] disabled:cursor-default disabled:opacity-60 disabled:hover:border-indigo-200 disabled:hover:shadow-none"
            >
              <FiBook className="size-5 text-indigo-600" />
              <div className="min-w-0 flex-1">
                <div className="text-xs font-semibold text-indigo-700">
                  Curriculum Metadata
                </div>
                <div className="text-[10px] text-indigo-600">
                  {availableTabs.length === 0 && liveCurriculumLoading
                    ? "Loading…"
                    : "Click to view details"}
                </div>
              </div>
              <FiEye className="size-3.5 text-indigo-500 opacity-0 transition-opacity group-hover:opacity-100" />
            </button>
          </div>

          {/* Curriculum Metadata Tabbed Modal */}
          {showCurriculumModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
              {/* Backdrop */}
              <button
                type="button"
                className="absolute inset-0 cursor-default"
                onClick={closeCurriculumModal}
                onKeyDown={(e) => {
                  if (e.key === "Escape") {
                    closeCurriculumModal();
                  }
                }}
                aria-label="Close modal"
              />

              {/* Modal Content */}
              <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="curriculum-modal-title"
                className="relative z-10 flex h-[85vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
              >
                {/* Header */}
                <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-4 py-3">
                  <div className="flex items-center gap-2">
                    <FiBook className="size-5 text-indigo-600" />
                    <h3
                      id="curriculum-modal-title"
                      className="text-lg font-semibold text-gray-800"
                    >
                      Curriculum Metadata
                    </h3>
                  </div>
                  <div className="flex items-center gap-2">
                    {/* UI / JSON Toggle */}
                    <div className="flex items-center rounded-lg bg-gray-200 p-0.5">
                      <button
                        onClick={() => setMetadataViewMode("ui")}
                        className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                          metadataViewMode === "ui"
                            ? "bg-white text-gray-900 shadow-sm"
                            : "text-gray-500 hover:text-gray-700"
                        }`}
                      >
                        <FiEye className="size-3.5" />
                        UI
                      </button>
                      <button
                        onClick={() => setMetadataViewMode("json")}
                        className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                          metadataViewMode === "json"
                            ? "bg-white text-gray-900 shadow-sm"
                            : "text-gray-500 hover:text-gray-700"
                        }`}
                      >
                        <FiCode className="size-3.5" />
                        JSON
                      </button>
                    </div>
                    <button
                      onClick={closeCurriculumModal}
                      className="rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-gray-200 hover:text-gray-700"
                    >
                      <FiX className="size-5" />
                    </button>
                  </div>
                </div>

                {metadataViewMode === "ui" ? (
                  <>
                    {/* Tabs */}
                    <div className="grid grid-cols-2 gap-2 bg-gray-50 px-4 py-3 sm:grid-cols-4">
                      {availableTabs.map((tab) => (
                        <button
                          key={tab.id}
                          onClick={() => setActiveTab(tab.id)}
                          className={`flex flex-col items-center justify-center gap-1.5 rounded-lg p-3 transition-all ${
                            activeTab === tab.id
                              ? `${tab.colors.bg} ${tab.colors.border} border ${tab.colors.text} shadow-sm`
                              : `border border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50`
                          }`}
                        >
                          <span className="text-xl">{tab.icon}</span>
                          <span className="text-center text-sm font-semibold leading-tight">
                            {tab.label}
                          </span>
                        </button>
                      ))}
                    </div>

                    {/* Content Panel */}
                    {currentTab && (
                      <div className="flex-1 overflow-y-auto bg-white p-5">
                        {/* Difficulty Badge for Difficulty Definitions tab */}
                        {currentTab.id === "difficultyDefinition" &&
                          request.difficulty && (
                            <div className="mb-4 flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-600">
                                Difficulty Level:
                              </span>
                              <span
                                className={`inline-flex items-center rounded-full px-3 py-1 text-sm font-semibold capitalize ${
                                  request.difficulty.toLowerCase() === "easy"
                                    ? "bg-green-100 text-green-700"
                                    : request.difficulty.toLowerCase() ===
                                        "medium"
                                      ? "bg-yellow-100 text-yellow-700"
                                      : request.difficulty.toLowerCase() ===
                                          "hard"
                                        ? "bg-red-100 text-red-700"
                                        : "bg-gray-100 text-gray-700"
                                }`}
                              >
                                {request.difficulty}
                              </span>
                            </div>
                          )}
                        {currentTab.isList &&
                        Array.isArray(currentTab.content) ? (
                          <div
                            className={`rounded-lg border ${currentTab.colors.borderIdle} ${currentTab.colors.bg} p-4`}
                          >
                            <ul className="space-y-2">
                              {currentTab.content.map((item, idx) => (
                                <li
                                  key={idx}
                                  className="flex items-start gap-2"
                                >
                                  <span
                                    className={`mt-1.5 size-1.5 shrink-0 rounded-full ${currentTab.colors.border} border bg-white`}
                                  />
                                  <span className="text-sm leading-relaxed text-gray-700">
                                    {formatDisplayValue(item)}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ) : (
                          renderStructuredContent(
                            currentTab.content,
                            currentTab.colors
                          )
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  /* JSON View */
                  <div className="flex-1 overflow-y-auto bg-white p-5">
                    <div className="mb-3 flex items-center justify-end">
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(
                            JSON.stringify(curriculumMetadata, null, 2)
                          );
                          setMetadataCopied(true);
                          setTimeout(() => setMetadataCopied(false), 2000);
                        }}
                        className="flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700"
                      >
                        {metadataCopied ? (
                          <>
                            <FiCheck className="size-3.5 text-green-500" />
                            Copied!
                          </>
                        ) : (
                          <>
                            <FiCopy className="size-3.5" />
                            Copy
                          </>
                        )}
                      </button>
                    </div>
                    <pre className="overflow-auto rounded-lg bg-gray-50 p-4 font-mono text-sm text-gray-700">
                      {JSON.stringify(curriculumMetadata, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {/* Instructions */}
      {request.instructions && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
          <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-amber-700">
            Instructions
          </div>
          <p className="text-sm text-amber-900">
            {formatDisplayValue(request.instructions)}
          </p>
        </div>
      )}
    </div>
  );
};

RequestColumn.propTypes = {
  request: PropTypes.object,
  curriculumMetadata: PropTypes.object,
  curriculum: PropTypes.string,
};

// ── Renderers are provided by @trilogy-innovations/content-renderers (GenerationColumn imported above) ──

// Helper to get rating config
const getRatingConfig = (rating) => {
  switch (rating?.toUpperCase()) {
    case "SUPERIOR":
      return {
        color: "bg-green-100 text-green-700",
        icon: FiCheckCircle,
        label: "SUPERIOR",
      };
    case "ACCEPTABLE":
      return {
        color: "bg-green-100 text-green-700",
        icon: FiCheckCircle,
        label: "ACCEPTABLE",
      };
    case "INFERIOR":
    default:
      return {
        color: "bg-orange-100 text-orange-700",
        icon: FiAlertCircle,
        label: rating || "INFERIOR",
      };
  }
};

// Evaluation Modal for detailed view
const EvaluationModal = ({ evaluation, onClose }) => {
  const [viewMode, setViewMode] = useState("ui");
  const [copied, setCopied] = useState(false);

  if (!evaluation) {
    return null;
  }

  const copyJson = () => {
    navigator.clipboard.writeText(JSON.stringify(evaluation, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const formatDisplayValue = (value) => {
    if (typeof value === "string") {
      return value;
    }
    if (typeof value === "number" || typeof value === "boolean") {
      return String(value);
    }
    if (Array.isArray(value)) {
      return value.map((item) => formatDisplayValue(item)).join(", ");
    }
    if (value && typeof value === "object") {
      return Object.entries(value)
        .map(
          ([key, nestedValue]) => `${key}: ${formatDisplayValue(nestedValue)}`
        )
        .join(" | ");
    }
    return "";
  };

  // Parse evaluation structure
  const evalData = evaluation.evaluation || {};
  const overall = evalData.overall || {};
  const overallRating = evalData.overall_rating || "INFERIOR";
  const score = evaluation.score ?? overall.score ?? 0;
  const ratingConfig = getRatingConfig(overallRating);

  // Get all dimension keys (excluding non-dimension fields)
  const excludedKeys = [
    "overall",
    "content_type",
    "overall_rating",
    "subcontent_evaluations",
  ];
  const dimensions = Object.entries(evalData).filter(
    ([key, value]) =>
      !excludedKeys.includes(key) &&
      typeof value === "object" &&
      value !== null &&
      "score" in value
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="flex h-[88vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        {/* Header */}
        <div className="flex shrink-0 items-center justify-between border-b border-gray-200 px-6 py-4">
          <div className="flex items-center gap-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Qualitative Results
            </h3>
            {/* UI/JSON Toggle */}
            <div className="flex items-center gap-1 rounded-lg bg-gray-100 p-1">
              <button
                onClick={() => setViewMode("ui")}
                className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                  viewMode === "ui"
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                <FiEye className="size-3.5" />
                UI
              </button>
              <button
                onClick={() => setViewMode("json")}
                className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                  viewMode === "json"
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                <FiCode className="size-3.5" />
                JSON
              </button>
            </div>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
          >
            <FiX className="size-5" />
          </button>
        </div>

        {/* Content */}
        <div className="min-h-0 flex-1 overflow-y-auto p-6">
          {viewMode === "ui" ? (
            <>
              {/* Overall Section */}
              <div className="mb-6">
                {/* Rating Badge and Score */}
                <div className="mb-4 flex items-center gap-4">
                  <div
                    className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-bold ${ratingConfig.color}`}
                  >
                    <ratingConfig.icon className="size-4" />
                    {ratingConfig.label}
                  </div>
                  <div className="text-2xl font-bold text-gray-900">
                    {(score * 100).toFixed(2)}%
                  </div>
                </div>

                {/* Overall Reasoning */}
                {overall.reasoning && (
                  <div
                    className={`mb-4 rounded-xl border-l-4 p-4 ${
                      overallRating === "INFERIOR"
                        ? "border-amber-500 bg-amber-50"
                        : "border-green-500 bg-green-50"
                    }`}
                  >
                    <p
                      className={`text-sm ${
                        overallRating === "INFERIOR"
                          ? "text-amber-800"
                          : "text-green-800"
                      }`}
                    >
                      {formatDisplayValue(overall.reasoning)}
                    </p>
                  </div>
                )}

                {/* Suggested Improvements */}
                {overall.suggested_improvements && (
                  <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                    <h4 className="mb-2 text-sm font-semibold text-amber-800">
                      Suggested Improvements
                    </h4>
                    <p className="text-sm text-amber-700">
                      {formatDisplayValue(overall.suggested_improvements)}
                    </p>
                  </div>
                )}
              </div>

              {/* Detailed Criteria */}
              {dimensions.length > 0 && (
                <div>
                  <h4 className="mb-4 text-sm font-semibold text-gray-700">
                    Detailed Criteria
                  </h4>
                  <div className="space-y-3">
                    {dimensions.map(([key, dimension]) => {
                      const dimScore = dimension.score ?? 0;
                      const isPassing = dimScore >= 0.85;
                      return (
                        <div
                          key={key}
                          className="rounded-xl border border-gray-200 bg-white p-4"
                        >
                          <div className="mb-2 flex items-center justify-between">
                            <h5 className="font-medium text-gray-900">
                              {key
                                .replace(/_/g, " ")
                                .replace(/\b\w/g, (l) => l.toUpperCase())}
                            </h5>
                            <span
                              className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-sm font-bold ${
                                isPassing
                                  ? "bg-green-100 text-green-700"
                                  : "bg-orange-100 text-orange-700"
                              }`}
                            >
                              {isPassing ? (
                                <FiCheckCircle className="size-3.5" />
                              ) : (
                                <FiAlertCircle className="size-3.5" />
                              )}
                              {isPassing ? "PASS" : "FAIL"}
                            </span>
                          </div>
                          {dimension.reasoning && (
                            <p className="text-sm text-gray-600">
                              {formatDisplayValue(dimension.reasoning)}
                            </p>
                          )}
                          {dimension.suggested_improvements && (
                            <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
                              <p className="text-xs font-medium text-amber-700">
                                💡{" "}
                                {formatDisplayValue(
                                  dimension.suggested_improvements
                                )}
                              </p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          ) : (
            /* JSON View */
            <div className="relative">
              <button
                onClick={copyJson}
                className="absolute right-2 top-2 rounded p-1.5 text-gray-400 transition-colors hover:bg-gray-200 hover:text-gray-600"
                title="Copy JSON"
              >
                {copied ? (
                  <FiCheck className="size-4 text-green-500" />
                ) : (
                  <FiCopy className="size-4" />
                )}
              </button>
              <pre className="overflow-auto rounded-lg bg-gray-50 p-4 pr-10 font-mono text-xs text-gray-700">
                {JSON.stringify(evaluation, null, 2)}
              </pre>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

EvaluationModal.propTypes = {
  evaluation: PropTypes.object,
  onClose: PropTypes.func.isRequired,
};

const NON_DIMENSION_EVAL_KEYS = new Set([
  "overall",
  "content_type",
  "overall_rating",
  "subcontent_evaluations",
]);

const formatDimensionName = (key) =>
  key
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const getFailedDimensionNames = (evalData) => {
  if (!evalData || typeof evalData !== "object") {
    return [];
  }
  const failed = [];
  for (const [key, value] of Object.entries(evalData)) {
    if (NON_DIMENSION_EVAL_KEYS.has(key)) {
      continue;
    }
    if (
      value &&
      typeof value === "object" &&
      typeof value.score === "number" &&
      value.score < 1
    ) {
      failed.push(formatDimensionName(key));
    }
  }
  return failed;
};

const FailedDimensionTags = ({ names }) => {
  if (!names || names.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-wrap justify-center gap-1.5">
      {names.map((name) => (
        <span
          key={name}
          className="relative inline-flex items-center whitespace-nowrap bg-red-400 py-0.5 pl-4 pr-2 text-[10px] font-medium text-white [clip-path:polygon(10px_0%,100%_0%,100%_100%,10px_100%,0%_50%)]"
        >
          <span className="absolute left-[7px] size-[5px] rounded-full bg-white/80" />
          {name}
        </span>
      ))}
    </div>
  );
};

FailedDimensionTags.propTypes = {
  names: PropTypes.arrayOf(PropTypes.string).isRequired,
};

// Evaluation Column - Shows score and overall rating
const EvaluationColumn = ({ evaluation, onShowDetails }) => {
  if (!evaluation) {
    return <div className="text-sm italic text-gray-400">Not evaluated</div>;
  }

  const evalData = evaluation.evaluation || {};
  const overallRating = evalData.overall_rating || "INFERIOR";
  const score = evaluation.score ?? evalData.overall?.score ?? 0;
  const scorePercent = (score * 100).toFixed(2);
  const ratingConfig = getRatingConfig(overallRating);
  const isPassing = overallRating !== "INFERIOR";
  const failedDimensions = isPassing ? [] : getFailedDimensionNames(evalData);

  return (
    <div className="flex flex-col items-center gap-3">
      {/* Score Circle */}
      <div
        className={`relative flex size-20 items-center justify-center rounded-full ${
          isPassing ? "bg-green-100" : "bg-orange-100"
        }`}
      >
        <div
          className={`text-2xl font-bold ${isPassing ? "text-green-600" : "text-orange-600"}`}
        >
          {scorePercent}%
        </div>
      </div>

      {/* Rating Badge */}
      <div
        className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-semibold ${ratingConfig.color}`}
      >
        <ratingConfig.icon className="size-4" />
        {ratingConfig.label}
      </div>

      <FailedDimensionTags names={failedDimensions} />

      {/* View Details Button */}
      <button
        onClick={onShowDetails}
        className="flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-900"
      >
        <FiMaximize2 className="size-3.5" />
        View Details
      </button>
    </div>
  );
};

EvaluationColumn.propTypes = {
  evaluation: PropTypes.object,
  onShowDetails: PropTypes.func.isRequired,
};

// Unified Item Row - Shows Request | Generation | Evaluation
const UnifiedItemRow = ({
  itemKey,
  request,
  curriculumMetadata,
  generation,
  evaluation,
  index,
  curriculum,
}) => {
  const [viewMode, setViewMode] = useState("ui"); // "ui" or "json"
  const [copiedSection, setCopiedSection] = useState(null);
  const [showEvalModal, setShowEvalModal] = useState(false);
  const [expandedJson, setExpandedJson] = useState(null); // "request" | "generation" | "evaluation" | null

  const copyToClipboard = (data, section) => {
    navigator.clipboard.writeText(JSON.stringify(data, null, 2));
    setCopiedSection(section);
    setTimeout(() => setCopiedSection(null), 2000);
  };

  const getExpandedData = () => {
    switch (expandedJson) {
      case "request":
        return { title: "Request JSON", data: request };
      case "generation":
        return { title: "Generation JSON", data: generation };
      case "evaluation":
        return { title: "Evaluation JSON", data: evaluation };
      default:
        return null;
    }
  };

  return (
    <>
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm transition-shadow hover:shadow-md">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-100 bg-gray-50 px-4 py-3">
          <div className="flex items-center gap-3">
            <span className="flex size-7 items-center justify-center rounded-lg bg-primary-100 text-sm font-bold text-primary-600">
              {index + 1}
            </span>
            <span className="font-mono text-xs text-gray-500">{itemKey}</span>
            {generation && typeof generation.timeTaken === "number" && (
              <span className="inline-flex items-center gap-1 rounded-md bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">
                <FiClock className="size-3" />
                {generation.timeTaken.toFixed(2)}s
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setViewMode("ui")}
              className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
                viewMode === "ui"
                  ? "bg-primary-100 text-primary-700"
                  : "text-gray-500 hover:bg-gray-100"
              }`}
            >
              <FiEye className="size-3.5" />
              UI
            </button>
            <button
              onClick={() => setViewMode("json")}
              className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
                viewMode === "json"
                  ? "bg-primary-100 text-primary-700"
                  : "text-gray-500 hover:bg-gray-100"
              }`}
            >
              <FiCode className="size-3.5" />
              JSON
            </button>
          </div>
        </div>

        {/* Content */}
        {viewMode === "ui" ? (
          <div className="flex flex-col divide-y divide-gray-100 lg:flex-row lg:divide-x lg:divide-y-0">
            {/* Request Column - fixed width */}
            <div className="p-4 lg:w-64 lg:shrink-0">
              <div className="mb-3 text-xs font-semibold uppercase tracking-wider text-gray-400">
                Request
              </div>
              <RequestColumn
                request={request}
                curriculumMetadata={curriculumMetadata}
                curriculum={curriculum}
              />
            </div>

            {/* Generation Column - flexible, takes remaining space */}
            <div className="min-w-0 flex-1 p-4">
              <div className="mb-3 text-xs font-semibold uppercase tracking-wider text-gray-400">
                Generation
              </div>
              <GenerationColumn generation={generation} />
            </div>

            {/* Evaluation Column - fixed width */}
            <div className="flex flex-col items-center justify-center p-4 lg:w-48 lg:shrink-0">
              <div className="mb-3 text-xs font-semibold uppercase tracking-wider text-gray-400">
                Evaluation
              </div>
              <EvaluationColumn
                evaluation={evaluation}
                onShowDetails={() => setShowEvalModal(true)}
              />
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 divide-y divide-gray-100 lg:grid-cols-3 lg:divide-x lg:divide-y-0">
            {/* Request JSON */}
            <div className="p-4">
              <div className="mb-3 flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">
                  Request
                </span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setExpandedJson("request")}
                    className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                    title="Expand"
                  >
                    <FiMaximize2 className="size-4" />
                  </button>
                  <button
                    onClick={() => copyToClipboard(request, "request")}
                    className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                    title="Copy"
                  >
                    {copiedSection === "request" ? (
                      <FiCheck className="size-4 text-green-500" />
                    ) : (
                      <FiCopy className="size-4" />
                    )}
                  </button>
                </div>
              </div>
              <pre className="max-h-64 overflow-auto rounded-lg bg-gray-50 p-3 font-mono text-xs text-gray-700">
                {JSON.stringify(request, null, 2)}
              </pre>
            </div>

            {/* Generation JSON */}
            <div className="p-4">
              <div className="mb-3 flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">
                  Generation
                </span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setExpandedJson("generation")}
                    className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                    title="Expand"
                  >
                    <FiMaximize2 className="size-4" />
                  </button>
                  <button
                    onClick={() => copyToClipboard(generation, "generation")}
                    className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                    title="Copy"
                  >
                    {copiedSection === "generation" ? (
                      <FiCheck className="size-4 text-green-500" />
                    ) : (
                      <FiCopy className="size-4" />
                    )}
                  </button>
                </div>
              </div>
              <pre className="max-h-64 overflow-auto rounded-lg bg-gray-50 p-3 font-mono text-xs text-gray-700">
                {JSON.stringify(generation, null, 2)}
              </pre>
            </div>

            {/* Evaluation JSON */}
            <div className="p-4">
              <div className="mb-3 flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">
                  Evaluation
                </span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setExpandedJson("evaluation")}
                    className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                    title="Expand"
                  >
                    <FiMaximize2 className="size-4" />
                  </button>
                  <button
                    onClick={() => copyToClipboard(evaluation, "evaluation")}
                    className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                    title="Copy"
                  >
                    {copiedSection === "evaluation" ? (
                      <FiCheck className="size-4 text-green-500" />
                    ) : (
                      <FiCopy className="size-4" />
                    )}
                  </button>
                </div>
              </div>
              <pre className="max-h-64 overflow-auto rounded-lg bg-gray-50 p-3 font-mono text-xs text-gray-700">
                {JSON.stringify(evaluation, null, 2)}
              </pre>
            </div>
          </div>
        )}
      </div>

      {/* Evaluation Modal */}
      {showEvalModal && (
        <EvaluationModal
          evaluation={evaluation}
          onClose={() => setShowEvalModal(false)}
        />
      )}

      {/* JSON Expand Modal */}
      {expandedJson && getExpandedData() && (
        <JsonModal
          title={getExpandedData().title}
          data={getExpandedData().data}
          onClose={() => setExpandedJson(null)}
        />
      )}
    </>
  );
};

UnifiedItemRow.propTypes = {
  itemKey: PropTypes.string.isRequired,
  request: PropTypes.object,
  curriculumMetadata: PropTypes.object,
  generation: PropTypes.object,
  evaluation: PropTypes.object,
  index: PropTypes.number.isRequired,
  curriculum: PropTypes.string,
};

// Pagination component
const Pagination = ({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  onItemsPerPageChange,
}) => {
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  // Generate page numbers to show
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          pages.push(i);
        }
        pages.push("...");
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push("...");
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push("...");
        pages.push(currentPage - 1);
        pages.push(currentPage);
        pages.push(currentPage + 1);
        pages.push("...");
        pages.push(totalPages);
      }
    }

    return pages;
  };

  return (
    <div className="flex flex-col items-center justify-between gap-4 border-t border-gray-200 bg-gray-50 px-4 py-3 sm:flex-row sm:px-6">
      {/* Items per page selector */}
      <div className="flex items-center gap-2">
        <span className="text-sm text-gray-500">Show</span>
        <select
          value={itemsPerPage}
          onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
          className="rounded-md border border-gray-300 bg-white px-2 py-1 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
        >
          <option value={10}>10</option>
          <option value={20}>20</option>
          <option value={50}>50</option>
          <option value={100}>100</option>
        </select>
        <span className="text-sm text-gray-500">per page</span>
      </div>

      {/* Showing X-Y of Z */}
      <div className="text-sm text-gray-500">
        Showing <span className="font-medium text-gray-900">{startItem}</span>{" "}
        to <span className="font-medium text-gray-900">{endItem}</span> of{" "}
        <span className="font-medium text-gray-900">{totalItems}</span> items
      </div>

      {/* Page navigation */}
      <div className="flex items-center gap-1">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="rounded-md p-1.5 text-gray-500 transition-colors hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
        >
          <FiChevronLeft className="size-5" />
        </button>

        {getPageNumbers().map((page, idx) =>
          page === "..." ? (
            <span key={`ellipsis-${idx}`} className="px-2 text-gray-400">
              ...
            </span>
          ) : (
            <button
              key={page}
              onClick={() => onPageChange(page)}
              className={`min-w-[32px] rounded-md px-2 py-1 text-sm font-medium transition-colors ${
                currentPage === page
                  ? "bg-primary-600 text-white"
                  : "text-gray-600 hover:bg-gray-200"
              }`}
            >
              {page}
            </button>
          )
        )}

        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="rounded-md p-1.5 text-gray-500 transition-colors hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
        >
          <FiChevronRight className="size-5" />
        </button>
      </div>
    </div>
  );
};

Pagination.propTypes = {
  currentPage: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  totalItems: PropTypes.number.isRequired,
  itemsPerPage: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onItemsPerPageChange: PropTypes.func.isRequired,
};

const RunDetail = () => {
  const { runId } = useParams();
  const navigate = useNavigate();
  const [run, setRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");

  // Filter states
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [selectedDifficulties, setSelectedDifficulties] = useState([]);
  const [selectedRequestCategories, setSelectedRequestCategories] = useState(
    []
  );
  const [showFailedOnly, setShowFailedOnly] = useState(false);

  // Related runs state
  const [relatedRuns, setRelatedRuns] = useState([]);

  // Variety check state
  const [varietyCheckLoading, setVarietyCheckLoading] = useState(false);
  const [varietyToggleLoading, setVarietyToggleLoading] = useState(false);

  const handleToggleVarietyCheck = async () => {
    if (!run || varietyToggleLoading) {
      return;
    }
    const newValue = !run.skip_variety_check;
    setVarietyToggleLoading(true);
    try {
      const { error: updateError } = await updateSkipVarietyCheck(
        run.id,
        newValue
      );
      if (!updateError) {
        setRun((prev) => ({ ...prev, skip_variety_check: newValue }));
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Failed to update variety check preference:", err);
    } finally {
      setVarietyToggleLoading(false);
    }
  };

  const handleVarietyCheck = async () => {
    setVarietyCheckLoading(true);
    try {
      await triggerVarietyCheck(run.id);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Variety check trigger failed:", err);
    } finally {
      try {
        await loadRun(true);
      } finally {
        setVarietyCheckLoading(false);
      }
    }
  };

  const copyRunId = () => {
    navigator.clipboard.writeText(run?.id || "");
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Handle items per page change
  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  // Handle search term change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  // Scroll to top when component mounts or runId changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [runId]);

  useEffect(() => {
    loadRun();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runId]);

  // Poll for updates if run is in progress
  useEffect(() => {
    if (!run) {
      return;
    }

    const inProgressStatuses = ["generating", "evaluating", "queued"];
    const isInProgress = inProgressStatuses.includes(run.status);

    if (!isInProgress) {
      return;
    }

    const interval = setInterval(() => {
      loadRun(true);
    }, 10000); // Poll every 10 seconds (silent refresh, no loading spinner)

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run?.status]);

  // Poll for variety check completion
  // Covers both: waiting for variety check to start (null) and while it's running
  useEffect(() => {
    if (!run || run.status !== "completed") {
      return;
    }
    // Stop polling once variety check is done (completed or failed)
    if (run.variety_status === "completed" || run.variety_status === "failed") {
      return;
    }

    const interval = setInterval(() => {
      loadRun(true);
    }, 10000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run?.status, run?.variety_status]);

  const loadRun = async (isPolling = false) => {
    // Only show full loading spinner on initial load, not during polling
    if (!isPolling) {
      setLoading(true);
    }
    setError(null);

    const { data, error: fetchError } = await fetchRun(runId);

    if (fetchError) {
      console.error("Error loading run:", fetchError);
      setError("Failed to load run details. The run may not exist.");
    } else {
      setRun(data);
      // Load related runs if this run has any
      loadRelatedRuns(data.related_run_ids);
    }

    if (!isPolling) {
      setLoading(false);
    }
  };

  const loadRelatedRuns = async (relatedRunIds) => {
    if (!relatedRunIds || relatedRunIds.length === 0) {
      setRelatedRuns([]);
      return;
    }

    const { data, error: fetchError } = await fetchRelatedRuns(relatedRunIds);

    if (fetchError) {
      console.error("Error loading related runs:", fetchError);
    } else {
      setRelatedRuns(data || []);
    }
  };

  // Create unified data by merging requests, generation, evaluation, and metadata by key
  const unifiedData = useMemo(() => {
    if (!run) {
      return [];
    }

    const requests = run.raw_requests || {};
    const generations = run.raw_generation_results || {};
    const evaluations = run.raw_evaluation_results || {};
    const curriculumMetadata = run.curriculum_metadata || {};

    // Get all unique keys from all data sources
    const allKeys = new Set([
      ...Object.keys(requests),
      ...Object.keys(generations),
      ...Object.keys(evaluations),
    ]);

    return Array.from(allKeys).map((key) => {
      const request = requests[key] || null;
      const metadata = curriculumMetadata[key] || null;

      return {
        key,
        request,
        curriculumMetadata: metadata,
        generation: generations[key] || null,
        evaluation: evaluations[key] || null,
      };
    });
  }, [run]);

  // Extract unique types, difficulties, and request categories for filter options
  const filterOptions = useMemo(() => {
    const types = new Set();
    const difficulties = new Set();
    const requestCategories = new Set();

    unifiedData.forEach((item) => {
      if (item.request?.type) {
        types.add(item.request.type.toLowerCase());
      }
      if (item.request?.difficulty) {
        difficulties.add(item.request.difficulty.toLowerCase());
      }
      if (item.request?.request_category) {
        requestCategories.add(item.request.request_category.toLowerCase());
      }
    });

    return {
      types: Array.from(types)
        .sort()
        .map((t) => ({ value: t, label: getTypeDisplayName(t) })),
      difficulties: Array.from(difficulties)
        .sort((a, b) => {
          const order = { easy: 1, medium: 2, hard: 3 };
          return (order[a] || 99) - (order[b] || 99);
        })
        .map((d) => ({
          value: d,
          label: d.charAt(0).toUpperCase() + d.slice(1),
        })),
      requestCategories: Array.from(requestCategories)
        .sort()
        .map((c) => ({
          value: c,
          label: getRequestCategoryConfig(c).label,
        })),
    };
  }, [unifiedData]);

  // Filter unified data based on search term and filters
  const filteredData = useMemo(() => {
    return unifiedData.filter((item) => {
      // Search filter - searches through item key (ID) and request content
      if (searchTerm.trim()) {
        const lowerSearch = searchTerm.toLowerCase().trim();
        // Check if item key (ID) matches
        const keyMatches = item.key?.toLowerCase().includes(lowerSearch);
        // Check if request content matches
        const requestStr = item.request
          ? JSON.stringify(item.request).toLowerCase()
          : "";
        const requestMatches = requestStr.includes(lowerSearch);
        // Return false only if neither matches
        if (!keyMatches && !requestMatches) {
          return false;
        }
      }

      // Type filter
      if (selectedTypes.length > 0) {
        const itemType = item.request?.type?.toLowerCase();
        if (!itemType || !selectedTypes.includes(itemType)) {
          return false;
        }
      }

      // Difficulty filter
      if (selectedDifficulties.length > 0) {
        const itemDifficulty = item.request?.difficulty?.toLowerCase();
        if (!itemDifficulty || !selectedDifficulties.includes(itemDifficulty)) {
          return false;
        }
      }

      // Request category filter
      if (selectedRequestCategories.length > 0) {
        const itemCategory = item.request?.request_category?.toLowerCase();
        if (
          !itemCategory ||
          !selectedRequestCategories.includes(itemCategory)
        ) {
          return false;
        }
      }

      // Failed items filter — pending items (no evaluation yet) are not
      // failures during an active run.
      if (showFailedOnly) {
        if (!item.evaluation) {
          return false;
        }
        const score = item.evaluation.score;
        if (typeof score === "number" && score >= 0.85) {
          return false;
        }
      }

      return true;
    });
  }, [
    unifiedData,
    searchTerm,
    selectedTypes,
    selectedDifficulties,
    selectedRequestCategories,
    showFailedOnly,
  ]);

  // Check if any filters are active (including search)
  const hasActiveFilters =
    selectedTypes.length > 0 ||
    selectedDifficulties.length > 0 ||
    selectedRequestCategories.length > 0 ||
    showFailedOnly ||
    searchTerm.trim();

  // Clear all filters including search
  const clearAllFilters = () => {
    setSelectedTypes([]);
    setSelectedDifficulties([]);
    setSelectedRequestCategories([]);
    setShowFailedOnly(false);
    setSearchTerm("");
    setCurrentPage(1);
  };

  const downloadAllJSON = () => {
    const allData = {
      requests: run.raw_requests,
      curriculum_metadata: run.curriculum_metadata,
      generation_results: run.raw_generation_results,
      evaluation_results: run.raw_evaluation_results,
    };
    const jsonStr = JSON.stringify(allData, null, 2);
    const blob = new Blob([jsonStr], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `run-${run.id}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const downloadFailedJSON = () => {
    const requests = run.raw_requests || {};
    const evaluations = run.raw_evaluation_results || {};
    const generations = run.raw_generation_results || {};
    const metadata = run.curriculum_metadata || {};

    const failedRequests = {};
    const failedMetadata = {};
    const failedGenerations = {};
    const failedEvaluations = {};

    for (const key of Object.keys(requests)) {
      const evalEntry = evaluations[key];
      if (!evalEntry) {
        continue;
      }
      const score = evalEntry.score;
      const isFailed = typeof score !== "number" || score < PASS_THRESHOLD;

      if (isFailed) {
        failedRequests[key] = requests[key];
        if (metadata[key]) {
          failedMetadata[key] = metadata[key];
        }
        if (generations[key]) {
          failedGenerations[key] = generations[key];
        }
        if (evaluations[key]) {
          failedEvaluations[key] = evaluations[key];
        }
      }
    }

    const failedData = {
      requests: failedRequests,
      curriculum_metadata: failedMetadata,
      generation_results: failedGenerations,
      evaluation_results: failedEvaluations,
    };
    const jsonStr = JSON.stringify(failedData, null, 2);
    const blob = new Blob([jsonStr], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `run-${run.id}-failed.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Pagination calculations (based on filtered data)
  const totalItems = filteredData.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedData = filteredData.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8 2xl:max-w-[1600px]">
          <div className="flex items-center justify-center py-24">
            <div className="text-center">
              <div className="mx-auto mb-4 size-12 animate-spin rounded-full border-y-2 border-primary-600"></div>
              <p className="text-sm text-gray-500">Loading run details...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !run) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8 2xl:max-w-[1600px]">
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <FiAlertCircle className="mb-4 size-12 text-red-400" />
            <h2 className="mb-2 text-xl font-semibold text-gray-900">
              Run Not Found
            </h2>
            <p className="mb-6 text-gray-500">
              {error || "The requested run could not be found."}
            </p>
            <button
              onClick={() => navigate("/past-runs")}
              className="flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-700"
            >
              <FiArrowLeft className="size-4" />
              Back to Past Runs
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8 2xl:max-w-[1600px]">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate("/past-runs")}
            className="mb-4 inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 transition-colors hover:text-primary-600"
          >
            <FiArrowLeft className="size-4" />
            Back to Past Runs
          </button>
          {/* Title + Status row */}
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h1 className="text-3xl font-bold text-gray-900">
              {run.bm_generators?.name || "Unknown Generator"} Run
            </h1>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">Status:</span>
              <span
                className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${getStatusConfig(run.status).bgColor} ${getStatusConfig(run.status).textColor}`}
              >
                {run.status === "completed" && (
                  <FiCheckCircle className="size-3" />
                )}
                {run.status === "failed" && (
                  <FiAlertCircle className="size-3" />
                )}
                {["generating", "evaluating", "queued"].includes(
                  run.status
                ) && (
                  <span
                    className={`size-2 animate-pulse rounded-full ${getStatusConfig(run.status).dotColor}`}
                  />
                )}
                {getStatusConfig(run.status).label}
              </span>
            </div>
          </div>

          {/* Run ID + Version chips row */}
          <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">Run ID:</span>
              <code className="rounded bg-gray-100 px-2 py-0.5 font-mono text-sm text-gray-700">
                {run.id}
              </code>
              <button
                onClick={copyRunId}
                className="rounded p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                title={copied ? "Copied!" : "Copy Run ID"}
              >
                {copied ? (
                  <FiCheck className="size-4 text-green-500" />
                ) : (
                  <FiCopy className="size-4" />
                )}
              </button>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {/* InceptBench Version */}
              {run.inceptbench_version && (
                <>
                  <span className="inline-flex items-center rounded-full bg-indigo-50 px-2.5 py-0.5 text-sm text-indigo-600">
                    InceptBench{" "}
                    <span className="ml-1 font-semibold">
                      v{run.inceptbench_version}
                    </span>
                  </span>
                  {run.inceptbench_version === "2.3.5" && (
                    <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-sm font-medium text-amber-700">
                      Experimental
                    </span>
                  )}
                </>
              )}

              {/* Dataset Version */}
              {run.dataset_version && (
                <>
                  <span className="text-gray-300">|</span>
                  <span className="inline-flex items-center rounded-full bg-emerald-50 px-2.5 py-0.5 text-sm text-emerald-600">
                    Benchmark Dataset{" "}
                    <span className="ml-1 font-semibold">
                      v{run.dataset_version}
                    </span>
                  </span>
                </>
              )}

              {/* Curriculum Version */}
              {run.curriculum_version && (
                <>
                  <span className="text-gray-300">|</span>
                  <span className="inline-flex items-center rounded-full bg-amber-50 px-2.5 py-0.5 text-sm text-amber-600">
                    Curriculum{" "}
                    <span className="ml-1 font-semibold">
                      v{run.curriculum_version}
                    </span>
                  </span>
                </>
              )}
            </div>
          </div>

          {/* Actions Row */}
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <span className="text-sm text-gray-500">Actions:</span>
            <RerunButton run={run} onRerunStarted={loadRun} />
            <ReEvaluateButton run={run} />
            {run.status === "completed" &&
              (!run.variety_status ||
                run.variety_status === "failed" ||
                (run.variety_status === "completed" &&
                  run.variety_score === null)) && (
                <button
                  onClick={handleVarietyCheck}
                  disabled={varietyCheckLoading}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:border-purple-200 hover:bg-purple-50 hover:text-purple-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {varietyCheckLoading ? (
                    <FiRefreshCw className="size-3.5 animate-spin" />
                  ) : (
                    <FiShuffle className="size-3.5" />
                  )}
                  {varietyCheckLoading
                    ? "Triggering..."
                    : run.variety_status === "failed" ||
                        (run.variety_status === "completed" &&
                          run.variety_score === null)
                      ? "Retry Variety Check"
                      : "Run Variety Check"}
                </button>
              )}
            {/* Variety Check Toggle — shown while run is in progress (not yet completed) */}
            {["queued", "generating", "evaluating"].includes(run.status) && (
              <div className="group/vc relative">
                <button
                  onClick={handleToggleVarietyCheck}
                  disabled={varietyToggleLoading || run.skip_variety_check}
                  className={`inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm font-medium transition-all disabled:cursor-not-allowed ${
                    run.skip_variety_check
                      ? "border-gray-200 bg-gray-50 text-gray-400"
                      : "border-purple-200 bg-purple-50 text-purple-700 hover:border-purple-300 hover:bg-purple-100"
                  }`}
                  role="switch"
                  aria-checked={!run.skip_variety_check}
                  aria-label="Toggle variety check"
                >
                  <FiShuffle className="size-3.5" />
                  <span>Variety Check</span>
                  <span
                    className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors duration-200 ${
                      run.skip_variety_check ? "bg-gray-300" : "bg-purple-500"
                    }`}
                  >
                    <span
                      className={`inline-block size-3.5 rounded-full bg-white shadow transition-transform duration-200 ${
                        run.skip_variety_check
                          ? "translate-x-0.5"
                          : "translate-x-[1.125rem]"
                      }`}
                    />
                  </span>
                </button>
                <div className="invisible absolute bottom-full left-1/2 z-10 mb-2 w-64 -translate-x-1/2 rounded-lg bg-gray-900 px-3 py-2 text-center text-xs leading-relaxed text-gray-200 shadow-lg group-hover/vc:visible">
                  {run.skip_variety_check ? (
                    <>
                      Turned off.
                      <br />
                      You can run it manually after completion.
                    </>
                  ) : (
                    <>
                      Auto-checks for static content after completion. Can be
                      run manually later.
                    </>
                  )}
                  <div className="absolute left-1/2 top-full -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Related Runs Section */}
        {relatedRuns.length > 0 && (
          <div className="mb-6 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
            <div className="border-b border-gray-200 bg-gradient-to-r from-indigo-50 to-purple-50 px-5 py-3">
              <div className="flex items-center gap-2">
                <FiRefreshCw className="size-4 text-indigo-600" />
                <h3 className="text-sm font-semibold text-gray-900">
                  Related Runs ({relatedRuns.length})
                </h3>
              </div>
            </div>
            <div className="space-y-2 p-4">
              {relatedRuns.map((relatedRun) => (
                <div
                  key={relatedRun.id}
                  className="group flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white p-2.5 shadow-sm transition-all hover:border-indigo-200 hover:shadow-md"
                >
                  <div className="flex min-w-0 flex-1 items-center gap-2">
                    <FiRefreshCw className="size-3.5 shrink-0 text-indigo-600" />
                    <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
                      <span className="font-mono text-xs font-medium text-gray-900">
                        {relatedRun.id.substring(0, 8)}...
                      </span>
                      <span
                        className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-xs font-medium ${getStatusConfig(relatedRun.status).bgColor} ${getStatusConfig(relatedRun.status).textColor}`}
                      >
                        {relatedRun.status === "completed" && (
                          <FiCheckCircle className="size-2.5" />
                        )}
                        {relatedRun.status === "failed" && (
                          <FiAlertCircle className="size-2.5" />
                        )}
                        {getStatusConfig(relatedRun.status).label}
                      </span>
                      {relatedRun.inceptbench_version && (
                        <span className="inline-flex items-center rounded-full bg-gradient-to-r from-indigo-100 to-purple-100 px-2 py-0.5 text-xs font-semibold text-indigo-700">
                          v{relatedRun.inceptbench_version}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex shrink-0 items-center gap-3">
                    {(relatedRun.aggregate_score !== null ||
                      relatedRun.pass_rate !== null) && (
                      <div className="flex gap-3 border-r border-gray-200 pr-3 text-xs">
                        {relatedRun.aggregate_score !== null &&
                          relatedRun.aggregate_score !== undefined && (
                            <div className="text-center">
                              <div className="text-gray-500">Score</div>
                              <div className="mt-0.5 font-semibold text-gray-900">
                                {(
                                  parseFloat(relatedRun.aggregate_score) * 100
                                ).toFixed(2)}
                                %
                              </div>
                            </div>
                          )}
                        {relatedRun.pass_rate !== null &&
                          relatedRun.pass_rate !== undefined && (
                            <div className="text-center">
                              <div className="text-gray-500">Pass</div>
                              <div className="mt-0.5 font-semibold text-green-600">
                                {(
                                  parseFloat(relatedRun.pass_rate) * 100
                                ).toFixed(2)}
                                %
                              </div>
                            </div>
                          )}
                      </div>
                    )}

                    <button
                      onClick={() => navigate(`/past-runs/${relatedRun.id}`)}
                      className="flex items-center gap-1 rounded-md border border-gray-200 bg-gray-50 px-2 py-1 text-xs font-medium text-gray-700 transition-all hover:border-indigo-200 hover:bg-indigo-50 hover:text-indigo-700"
                    >
                      <FiEye className="size-3" />
                      View
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Main Content Card */}
        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
          {/* Run Details Section */}
          <RunDetailsSection run={run} />

          {/* Insights Section */}
          <InsightsSection run={run} />

          {/* Run Items section (visible in all states — shows items generated/evaluated so far) */}
          {run.status && (
            <>
              {/* Unified View Header */}
              <div className="border-b border-gray-200 bg-gray-50 px-6 py-4">
                {/* Top row: Title + Download */}
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Run Items
                    </h3>
                    <p className="text-sm text-gray-500">
                      Request, Generation, and Evaluation for each item
                    </p>
                  </div>
                  <button
                    onClick={
                      showFailedOnly ? downloadFailedJSON : downloadAllJSON
                    }
                    className="flex items-center justify-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-700"
                  >
                    <FiDownload className="size-4" />
                    {showFailedOnly
                      ? "Download Failed JSON"
                      : "Download All JSON"}
                  </button>
                </div>

                {/* Filters row */}
                <div className="mt-4 flex flex-wrap items-center gap-3">
                  {/* Left side: Filter dropdowns + Search */}
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <FiFilter className="size-4" />
                      <span>Filters:</span>
                    </div>

                    {/* Type Filter */}
                    <MultiSelectDropdown
                      options={filterOptions.types}
                      selectedValues={selectedTypes}
                      onChange={(values) => {
                        setSelectedTypes(values);
                        setCurrentPage(1);
                      }}
                      placeholder="Type"
                      width="w-28"
                    />

                    {/* Difficulty Filter */}
                    <MultiSelectDropdown
                      options={filterOptions.difficulties}
                      selectedValues={selectedDifficulties}
                      onChange={(values) => {
                        setSelectedDifficulties(values);
                        setCurrentPage(1);
                      }}
                      placeholder="Difficulty"
                      width="w-28"
                    />

                    {/* Category Filter */}
                    {filterOptions.requestCategories.length > 1 && (
                      <MultiSelectDropdown
                        options={filterOptions.requestCategories}
                        selectedValues={selectedRequestCategories}
                        onChange={(values) => {
                          setSelectedRequestCategories(values);
                          setCurrentPage(1);
                        }}
                        placeholder="Category"
                        width="w-28"
                      />
                    )}

                    {/* Search Box */}
                    <div className="relative">
                      <FiSearch className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search ID or Request..."
                        value={searchTerm}
                        onChange={handleSearchChange}
                        className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-8 text-sm transition-colors placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 sm:w-52"
                      />
                      {searchTerm && (
                        <button
                          onClick={() => {
                            setSearchTerm("");
                            setCurrentPage(1);
                          }}
                          className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-gray-400 hover:text-gray-600"
                        >
                          <FiX className="size-4" />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Right side: Failed toggle only */}
                  <div className="ml-auto flex items-center gap-2">
                    <label className="flex cursor-pointer items-center gap-2">
                      <span className="text-sm text-gray-600">Failed only</span>
                      <div className="relative">
                        <input
                          type="checkbox"
                          checked={showFailedOnly}
                          onChange={(e) => {
                            setShowFailedOnly(e.target.checked);
                            setCurrentPage(1);
                          }}
                          className="peer sr-only"
                        />
                        <div className="h-5 w-9 rounded-full bg-gray-300 transition-colors peer-checked:bg-orange-500"></div>
                        <div className="absolute left-0.5 top-0.5 size-4 rounded-full bg-white shadow-sm transition-transform peer-checked:translate-x-4"></div>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Active filters display */}
                {hasActiveFilters && (
                  <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                    {/* Left: Label + Applied filter chips */}
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm text-gray-500">
                        Active filters:
                      </span>
                      {selectedTypes.map((type) => (
                        <span
                          key={`type-${type}`}
                          className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2.5 py-1 text-xs font-medium text-blue-700"
                        >
                          {type.toUpperCase()}
                          <button
                            onClick={() => {
                              setSelectedTypes(
                                selectedTypes.filter((t) => t !== type)
                              );
                              setCurrentPage(1);
                            }}
                            className="ml-0.5 rounded-full p-0.5 hover:bg-blue-200"
                          >
                            <FiX className="size-3" />
                          </button>
                        </span>
                      ))}
                      {selectedDifficulties.map((diff) => (
                        <span
                          key={`diff-${diff}`}
                          className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${
                            diff === "easy"
                              ? "bg-green-100 text-green-700"
                              : diff === "medium"
                                ? "bg-yellow-100 text-yellow-700"
                                : "bg-red-100 text-red-700"
                          }`}
                        >
                          {diff.charAt(0).toUpperCase() + diff.slice(1)}
                          <button
                            onClick={() => {
                              setSelectedDifficulties(
                                selectedDifficulties.filter((d) => d !== diff)
                              );
                              setCurrentPage(1);
                            }}
                            className="ml-0.5 rounded-full p-0.5 hover:bg-gray-200/50"
                          >
                            <FiX className="size-3" />
                          </button>
                        </span>
                      ))}
                      {selectedRequestCategories.map((cat) => {
                        const catConfig = getRequestCategoryConfig(cat);
                        return (
                          <span
                            key={`cat-${cat}`}
                            className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${catConfig.bgColor} ${catConfig.color}`}
                          >
                            {catConfig.label}
                            <button
                              onClick={() => {
                                setSelectedRequestCategories(
                                  selectedRequestCategories.filter(
                                    (c) => c !== cat
                                  )
                                );
                                setCurrentPage(1);
                              }}
                              className="ml-0.5 rounded-full p-0.5 hover:bg-gray-200/50"
                            >
                              <FiX className="size-3" />
                            </button>
                          </span>
                        );
                      })}
                      {showFailedOnly && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-orange-100 px-2.5 py-1 text-xs font-medium text-orange-700">
                          Failed only
                          <button
                            onClick={() => {
                              setShowFailedOnly(false);
                              setCurrentPage(1);
                            }}
                            className="ml-0.5 rounded-full p-0.5 hover:bg-orange-200"
                          >
                            <FiX className="size-3" />
                          </button>
                        </span>
                      )}
                      {searchTerm.trim() && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-gray-200 px-2.5 py-1 text-xs font-medium text-gray-700">
                          {searchTerm}
                          <button
                            onClick={() => {
                              setSearchTerm("");
                              setCurrentPage(1);
                            }}
                            className="ml-0.5 rounded-full p-0.5 hover:bg-gray-300"
                          >
                            <FiX className="size-3" />
                          </button>
                        </span>
                      )}
                    </div>

                    {/* Right: Count + Clear all button */}
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-gray-500">
                        <span className="font-semibold text-gray-700">
                          {filteredData.length}
                        </span>{" "}
                        items found
                      </span>
                      <button
                        onClick={clearAllFilters}
                        className="flex items-center gap-1 rounded-lg border border-red-200 bg-red-50 px-3 py-1 text-sm font-medium text-red-600 transition-colors hover:bg-red-100"
                      >
                        <FiX className="size-3.5" />
                        Clear All
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Unified View Content */}
              <div className="p-6">
                {unifiedData.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <p className="text-gray-500">No data available</p>
                  </div>
                ) : filteredData.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <FiSearch className="mb-3 size-10 text-gray-300" />
                    <p className="font-medium text-gray-700">
                      No matching items
                    </p>
                    <p className="mt-1 text-sm text-gray-500">
                      No requests match &quot;{searchTerm}&quot;
                    </p>
                    <button
                      onClick={() => {
                        setSearchTerm("");
                        setCurrentPage(1);
                      }}
                      className="mt-3 text-sm text-primary-600 hover:text-primary-700 hover:underline"
                    >
                      Clear search
                    </button>
                  </div>
                ) : (
                  <>
                    {/* Top Pagination */}
                    {totalPages > 1 && (
                      <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        totalItems={totalItems}
                        itemsPerPage={itemsPerPage}
                        onPageChange={setCurrentPage}
                        onItemsPerPageChange={handleItemsPerPageChange}
                      />
                    )}

                    {/* Unified Item Rows */}
                    <div className="space-y-4 py-4">
                      {paginatedData.map((item, index) => (
                        <UnifiedItemRow
                          key={item.key}
                          itemKey={item.key}
                          request={item.request}
                          curriculumMetadata={item.curriculumMetadata}
                          generation={item.generation}
                          evaluation={item.evaluation}
                          index={startIndex + index}
                          curriculum={run?.filters?.curriculum}
                        />
                      ))}
                    </div>

                    {/* Bottom Pagination */}
                    {totalPages > 1 && (
                      <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        totalItems={totalItems}
                        itemsPerPage={itemsPerPage}
                        onPageChange={setCurrentPage}
                        onItemsPerPageChange={handleItemsPerPageChange}
                      />
                    )}
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default RunDetail;

// Named exports for testing — re-exported from the shared package
export {
  FillInRenderer,
  MCQSetRenderer,
  SAQRenderer,
  LEQRenderer,
  DBQRenderer,
} from "@trilogy-innovations/content-renderers";
